import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import { Subject, Observable, timer, combineLatest, of, Subscription } from 'rxjs';
import { takeUntil, debounceTime, switchMap, catchError, distinctUntilChanged, filter, tap, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CircularProgressComponent } from './circular-progress.component';
// NEW: Quagga2 for improved barcode scanning
import Quagga from '@ericblade/quagga2';
// NEW: Tesseract.js for OCR text detection fallback
import Tesseract, { createWorker, Worker as TesseractWorker } from 'tesseract.js';
import {
  MobileScanningService,
  ScanSession,
  CartonScanRequest,
  CartonScanResponse,
  StartSessionRequest,
  ScanWithStatusResult,
  ScanningProgress,
  OrderScanningStatus,
  PreAlertScanningMetrics,
  CartonListItem,
  PreAlertCartonList,
  CartonListFilter,
  CartonScanSummary
} from '../../services/mobile-scanning.service';
import { SignalRService, ScanningProgressUpdate, ScanActivityBroadcast, CollaborativeScanUpdate } from '../../services/signalr.service';
import { OrderStatusUpdate } from '../../services/signalr.service';
import { ScanProgressSignalRService, ScanProgressUpdate } from '../../services/scan-progress-signalr.service';
import * as signalR from '@microsoft/signalr';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { ErrorLogService } from '../../services/error-log.service';
import confetti from 'canvas-confetti';

export interface CartonScanItem {
  cartonNo: string;
  scannedAt: Date;
  isExpected: boolean;
  orderId?: string;
  status: 'scanned' | 'matched' | 'unexpected';
}

export interface CartonMatchingConfig {
  expectedCartons: string[];
  allowUnexpectedCartons: boolean;
  autoCompleteOnMatch: boolean;
  showProgress: boolean;
}

export interface CartonScanProgress {
  scannedCount: number;
  expectedCount: number;
  matchedCount: number;
  unexpectedCount: number;
  shortageCount: number;
  isComplete: boolean;
  completionPercentage: number;
}

// === NEW ORDER STATUS INTEGRATION INTERFACES ===

// Note: OrderStatusUpdate interface is imported from SignalRService
// Local interface removed to avoid conflicts

export interface ScanningDashboard {
  activeOrders: {
    orderId: string;
    completionPercentage: number;
    nextStatus?: string;
    blockers: string[];
  }[];
  recentActivity: {
    cartonNo: string;
    timestamp: Date;
    status: string;
    orderId: string;
  }[];
  systemMetrics: {
    totalCartonsToday: number;
    averageScanRate: number;
    completedOrders: number;
    pendingOrders: number;
  };
}

export interface ScanSessionConfig {
  orderId: string;
  sessionType: 'mobile_scan' | 'warehouse_scan';
  autoSaveInterval: number; // milliseconds
  enableOfflineMode: boolean;
  showRealTimeProgress: boolean;
  audioFeedback: boolean;
  vibrationFeedback: boolean;
}

// === PHASE 5: DUPLICATE SCAN WARNING INTERFACES ===

export interface DuplicateScanInfo {
  cartonNumber: string;
  ladingBill: string;
  isDuplicate: boolean;
  previousScan: PreviousScanDetails;
  message: string;
}

export interface PreviousScanDetails {
  scannedBy: string;
  scannedAt: Date;
  scannedAtFormatted: string;
  minutesAgo: number;
  isSameUser: boolean;
}

export interface ScanHistoryEntry {
  scanAtUtc: Date;
  scannedBy: string;
  ipAddress: string;
  action: string;
}

export interface ScanHistory {
  cartonNumber: string;
  totalScans: number;
  firstScanAt: Date;
  lastScanAt: Date;
  scanAttempts: ScanHistoryEntry[];
}

// === NEW: OCR INTERFACES ===

export interface OcrResult {
  text: string;
  confidence: number;
  timestamp: Date;
}

export interface ScannerMode {
  mode: 'quagga' | 'ocr' | 'manual';
  reason?: string;
}

@Component({
  selector: 'app-mobile-carton-scanner',
  standalone: true,
  imports: [CommonModule, FormsModule, CircularProgressComponent],
  templateUrl: './mobile-carton-scanner.component.html',
  styleUrl: './mobile-carton-scanner.component.css',
  providers: [],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('scaleIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1)' }))
      ])
    ])
  ]
})
export class MobileCartonScannerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  @Input() config: CartonMatchingConfig = {
    expectedCartons: [],
    allowUnexpectedCartons: true,
    autoCompleteOnMatch: false,
    showProgress: true
  };

  @Input() orderId?: string;
  @Input() disabled: boolean = false;
  @Input() showHistory: boolean = true;
  /**
   * Maximum history items to keep (fallback if no pre-alert selected)
   * When scanning with a pre-alert, the limit automatically adjusts to pre-alert's totalCartons
   */
  @Input() maxHistory: number = 500; // Increased from 100 to accommodate larger pre-alerts
  @Input() sessionConfig: ScanSessionConfig = {
    orderId: '',
    sessionType: 'mobile_scan',
    autoSaveInterval: 30000, // 30 seconds
    enableOfflineMode: true,
    showRealTimeProgress: true,
    audioFeedback: true,
    vibrationFeedback: true
  };
  @Input() enableSessionManagement: boolean = true;

  @Output() cartonScanned = new EventEmitter<CartonScanItem>();
  @Output() progressChanged = new EventEmitter<CartonScanProgress>();
  @Output() scanCompleted = new EventEmitter<CartonScanItem[]>();
  @Output() shortageDetected = new EventEmitter<string[]>();
  @Output() sessionStarted = new EventEmitter<ScanSession>();
  @Output() sessionCompleted = new EventEmitter<any>();
  @Output() scanError = new EventEmitter<string>();

  // === NEW ORDER STATUS INTEGRATION OUTPUTS ===
  @Output() orderStatusUpdated = new EventEmitter<OrderStatusUpdate>();
  @Output() scanningProgressUpdated = new EventEmitter<ScanningProgress>();
  @Output() workflowAdvanced = new EventEmitter<{
    orderId: string;
    fromStatus: string;
    toStatus: string;
    completionPercentage: number;
  }>();

  // Scanner state
  manualEntry = false;
  manualCartonNo = '';
  
  // Camera scanner state
  cameraEnabled = false;
  availableDevices: MediaDeviceInfo[] = [];
  selectedDevice?: MediaDeviceInfo;
  hasDevicePermission = false;
  cameraScannerActive = false;
  torchEnabled = false;
  torchAvailable = false;

  // === NEW: Quagga2 & OCR State ===
  @ViewChild('quaggaScanner', { static: false }) quaggaScannerElement?: ElementRef;
  private quaggaInitialized = false;
  private tesseractWorker?: TesseractWorker;
  usingQuagga2 = false; // Track if using Quagga2
  scannerMode: ScannerMode = { mode: 'quagga' };

  // OCR state
  ocrProgress = 0;
  showOcrConfirmDialog = false;
  currentOcrResult?: OcrResult;
  ocrProcessing = false;
  scanningStatus = '';
  private ocrConfirmCallback?: (result: boolean) => void;

  // Barcode quality priority state
  private pendingBarcodeResult: { code: string, quality: number, timeout: any } | null = null;

  // Smart prefix detection for OCR auto-completion
  private detectedCartonPrefixes: Set<string> = new Set();
  private commonCartonPrefix: string = 'TWSPKM'; // Default fallback

  // Scanning results
  scannedCartons: CartonScanItem[] = [];
  progress: CartonScanProgress = {
    scannedCount: 0,
    expectedCount: 0,
    matchedCount: 0,
    unexpectedCount: 0,
    shortageCount: 0,
    isComplete: false,
    completionPercentage: 0
  };

  // Session management
  currentSession$: Observable<ScanSession | null>;
  currentSession: ScanSession | null = null;
  sessionActive = false;
  sessionPaused = false;
  sessionStartTime: Date | null = null;
  lastScanTime: Date | null = null;
  autoSaveTimer: any = null;
  
  // API integration state
  apiConnected = true;
  offlineScans: CartonScanRequest[] = [];
  syncInProgress = false;

  // === BATCHED STATUS UPDATE SYSTEM ===
  pendingStatusUpdates: string[] = []; // Queue of carton numbers awaiting status update
  batchUpdateInProgress = false;
  lastBatchUpdateTime: Date | null = null;
  readonly BATCH_SIZE = 20; // Update every 20 cartons
  readonly BATCH_TIMEOUT_MS = 30000; // Or every 30 seconds
  batchUpdateTimer: any = null;
  
  // UI state
  showAlert = false;
  alertMessage = '';
  alertType: 'success' | 'warning' | 'error' = 'success';
  showSessionControls = false;
  showProgressDetails = false;
  showOfflineIndicator = false;

  // === NEW ORDER STATUS INTEGRATION STATE ===

  // Order status tracking
  currentOrderStatus: OrderScanningStatus | null = null;
  orderStatusUpdateInProgress = false;
  preAlertMetrics: PreAlertScanningMetrics[] = [];

  // Real-time progress monitoring
  enableRealTimeUpdates = true;
  statusUpdateInterval = 30000; // 30 seconds
  progressMonitoringActive = false;

  // Status transition notifications
  showStatusTransition = false;
  statusTransitionMessage = '';
  lastOrderStatusUpdate: OrderStatusUpdate | null = null;

  // Scanning analytics
  sessionAnalytics = {
    scanRate: 0, // scans per minute
    averageTimePerScan: 0, // seconds
    statusUpdatesTriggered: 0,
    workflowAdvancementCount: 0
  };

  // Enhanced UI controls
  showOrderStatusPanel = true;
  showPreAlertProgress = true;
  showScanningAnalytics = false;
  showWorkflowPreview = true;

  // === SIGNALR REAL-TIME STATE ===

  // Real-time scanning activity
  realtimeScanActivity: ScanActivityBroadcast[] = [];
  activeScanners: Map<string, { userName: string; lastActivity: Date; scanCount: number }> = new Map();

  // Real-time progress updates
  realtimeProgressUpdates: ScanningProgressUpdate[] = [];

  // Real-time order status updates
  realtimeOrderStatusUpdates: OrderStatusUpdate[] = [];

  // Collaborative scanning
  collaborativeSession: CollaborativeScanUpdate | null = null;
  isCollaborativeMode = false;

  // Real-time notifications
  showRealtimeNotifications = true;
  notificationQueue: { type: string; message: string; timestamp: Date }[] = [];

  // SignalR connection status
  signalRConnected = false;
  signalRReconnecting = false;

  // === CARTON LIST STATE ===

  // Current pre-alert carton list
  currentCartonList: PreAlertCartonList | null = null;
  cartonListLoading = false;
  cartonListError: string | null = null;

  // Carton list filtering and display
  cartonListFilter: CartonListFilter = {
    status: 'all',
    sortBy: 'cartonNumber',
    sortDirection: 'asc'
  };

  // UI state for carton list panel
  showCartonListPanel = false;
  cartonListPanelCollapsed = false;
  activeCartonListTab: 'all' | 'scanned' | 'unscanned' = 'all';

  // Search functionality
  cartonSearchTerm = '';
  searchResults: CartonListItem[] = [];

  // === BULK UPDATE STATE ===
  bulkUpdateInProgress = false;
  bulkUpdateResults: any | null = null;
  showBulkUpdateResults = false;

  // === SCAN PROGRESS SIGNALR STATE (NEW) ===

  // Real-time scan progress from dedicated SignalR service
  scanProgressRT: ScanProgressUpdate | null = null;
  isSignalRProgressConnected = false;
  private scanProgressSubscription?: Subscription;
  private currentPreAlertLadingBill: string | null = null;

  // UI state for modern progress display
  showModernProgressCard = false;
  recentProgressScans: Array<{cartonNumber: string, timestamp: Date, scannedBy: string}> = [];
  progressScanAnimation = false;

  // === PHASE 4: ENHANCED UX CELEBRATION STATE ===

  // Completion celebration
  showCompletionModal = false;
  private hasTriggeredCelebration = false;

  // Settings and preferences
  showSettings = false;
  showConfigPanel = false; // Phase 1: Collapsible config panel
  soundEnabled = true;
  hapticEnabled = true;

  // Audio context for sound effects
  private audioContext?: AudioContext;

  // === PHASE 5: DUPLICATE SCAN WARNING STATE ===

  showDuplicateWarning = false;
  duplicateScanInfo: DuplicateScanInfo | null = null;
  scanHistory: ScanHistory | null = null;
  loadingScanHistory = false;
  isAdminUser = false;

  // === AUTO-DETECTION STATE ===

  // Auto-detected pre-alert information
  autoDetectedPreAlert: any = null; // Will match PreAlertInfo from backend
  isDetectingPreAlert = false;
  preAlertLocked = false;
  showPreAlertInfo = true;

  // Auto-detection configuration
  enableAutoDetection = true;
  autoShowCartonListOnDetection = true;
  validateCartonsAgainstPreAlert = true;

  // Auto-refresh carton list
  cartonListAutoRefresh = true;
  cartonListRefreshInterval = 15000; // 15 seconds

  constructor(
    private mobileScanningService: MobileScanningService,
    private signalRService: SignalRService,
    private scanProgressSignalR: ScanProgressSignalRService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private http: HttpClient,
    private errorLogService: ErrorLogService
  ) {
    this.currentSession$ = this.mobileScanningService.currentSession$;
  }

  ngOnInit(): void {
    this.updateProgress();
    this.initializeSessionManagement();
    this.setupAutoSave();
    this.initializeCameraDevices();

    // === PHASE 4: INITIALIZE AUDIO AND PREFERENCES ===
    this.initializeAudioContext();
    this.loadUserPreferences();

    // === PHASE 5: CHECK USER ROLE FOR ADMIN FEATURES ===
    const currentUser = this.authService.getCurrentUser();
    this.isAdminUser = currentUser?.role === 'admin' || false;
    console.log('Admin user status:', this.isAdminUser);

    // === NEW ORDER STATUS INTEGRATION INITIALIZATION ===
    this.initializeOrderStatusTracking();
    this.setupRealTimeProgressMonitoring();
    this.initializeScanningAnalytics();

    // === SIGNALR REAL-TIME INTEGRATION ===
    this.initializeSignalRSubscriptions();

    // === CARTON LIST INITIALIZATION ===
    this.initializeCartonList();

    // === SCAN PROGRESS SIGNALR INITIALIZATION (NEW) ===
    this.initializeScanProgressSignalR();

    // === NEW: INITIALIZE TESSERACT OCR WORKER ===
    this.initializeTesseractWorker();

    // === PERFORMANCE FIX: LOAD BATCH UPDATE QUEUE ===
    this.loadBatchQueueFromStorage();

    // === SESSION PROTECTION: RESTORE PREVIOUS SESSION ===
    this.restoreSessionFromStorage();

    // === OFFLINE PERSISTENCE: LOAD PENDING SCANS AND SETUP NETWORK LISTENERS ===
    this.loadOfflineScansFromStorage();
    this.setupNetworkListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupSession();
    this.stopCameraScanner();

    // === NEW ORDER STATUS INTEGRATION CLEANUP ===
    this.stopProgressMonitoring();
    this.saveAnalyticsData();

    // === SIGNALR CLEANUP ===
    this.cleanupSignalRSubscriptions();

    // === SCAN PROGRESS SIGNALR CLEANUP (NEW) ===
    this.cleanupScanProgressSignalR();

    // === NEW: CLEANUP QUAGGA2 AND TESSERACT ===
    this.cleanupQuagga2();
    this.cleanupTesseractWorker();

    // === OFFLINE PERSISTENCE: SAVE ON EXIT ===
    if (this.offlineScans.length > 0) {
      this.saveOfflineScansToStorage();
    }
  }

  /**
   * Prevent page unload if there are unsynced offline scans
   */
  @HostListener('window:beforeunload', ['$event'])
  canDeactivate($event: any): boolean {
    if (this.offlineScans.length > 0) {
      const message = `⚠ You have ${this.offlineScans.length} unsynced scans. They will be saved and synced when you return online.`;
      $event.returnValue = message;
      // Save to storage before potential exit
      this.saveOfflineScansToStorage();
      return false;
    }
    return true;
  }

  onCameraScanSuccess(result: string): void {
    if (result && result.trim()) {
      this.processCartonScan(result.trim());
    }
  }
  
  // Removed ZXing camera scan handlers - using Quagga2 only

  async processCartonScan(cartonNo: string): Promise<void> {
    if (!cartonNo) return;

    // Learn carton prefix pattern for future OCR auto-completion
    this.learnCartonPrefix(cartonNo);

    // Check if already scanned
    if (this.scannedCartons.some(item => item.cartonNo === cartonNo)) {
      this.showAlertMessage(`Carton ${cartonNo} already scanned`, 'warning');
      this.playErrorSound();
      this.vibrate([100, 100, 100]);
      return;
    }

    // === AUTO-DETECTION LOGIC ===
    // Auto-detect pre-alert on first scan (if enabled and no pre-alert locked)
    if (this.enableAutoDetection && !this.autoDetectedPreAlert && !this.preAlertLocked) {
      await this.detectAndLoadPreAlert(cartonNo);
    }

    // Validate against detected pre-alert (if validation is enabled)
    if (this.validateCartonsAgainstPreAlert && this.autoDetectedPreAlert) {
      const isValid = this.validateCartonAgainstPreAlert(cartonNo);
      if (!isValid) {
        this.showAlertMessage(`Carton ${cartonNo} not found in current pre-alert ${this.autoDetectedPreAlert.ladingBill}`, 'warning');
        this.playErrorSound();
        this.vibrate([200, 100, 200]);
        return;
      }
    }

    // Determine if carton is expected (check currentCartonList first for auto-detected pre-alerts)
    const isExpected = (this.currentCartonList?.cartons.some(c => c.cartonNumber === cartonNo)) ||
                      this.config.expectedCartons.includes(cartonNo) ||
                      (this.autoDetectedPreAlert && this.isCartonInDetectedPreAlert(cartonNo));

    if (!isExpected && !this.config.allowUnexpectedCartons && !this.autoDetectedPreAlert) {
      this.showAlertMessage(`Unexpected carton ${cartonNo} not allowed`, 'error');
      this.playErrorSound();
      this.vibrate([200, 100, 200]);
      return;
    }

    // Process with API if session active
    if (this.enableSessionManagement && this.sessionActive && this.currentSession) {
      this.scanCartonWithAPI(cartonNo);
    } else {
      this.processCartonLocally(cartonNo);
    }

    // Update carton list status if pre-alert is detected
    if (this.autoDetectedPreAlert) {
      this.updateCartonListStatus(cartonNo);
    }

    // **PERFORMANCE FIX: Queue status update instead of immediate API call**
    // Instead of updating status to "Arrived" immediately (causing 140 HTTP requests),
    // we queue the carton and batch update every 20 cartons or 30 seconds
    this.queueStatusUpdate(cartonNo);

    this.lastScanTime = new Date();
  }

  private scanCartonWithAPI(cartonNo: string): void {
    if (!this.currentSession) return;

    const scanRequest: CartonScanRequest = {
      sessionId: this.currentSession.sessionId,
      cartonNo: cartonNo,
      scannedBy: this.authService.getCurrentUser()?.username || 'mobile_user',
      timestamp: new Date(),
      location: 'mobile_scan',
      notes: ''
    };

    if (this.apiConnected) {
      // Use enhanced scanning with status updates
      this.mobileScanningService.scanCartonWithStatusUpdate(scanRequest).pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Enhanced API scan failed:', error);

          // === PHASE 5: HANDLE DUPLICATE SCAN (HTTP 409) ===
          if (error.status === 409 && error.error?.isDuplicate) {
            this.handleDuplicateScan(error.error);
            return of(null as any);
          }

          this.apiConnected = false;
          this.showOfflineIndicator = true;
          // Fallback to basic scan
          return this.mobileScanningService.scanCarton(scanRequest).pipe(
            catchError((fallbackError) => {
              // Check for duplicate in fallback too
              if (fallbackError.status === 409 && fallbackError.error?.isDuplicate) {
                this.handleDuplicateScan(fallbackError.error);
                return of(null as any);
              }

              // Ultimate fallback - process locally
              this.processCartonLocally(cartonNo);
              this.offlineScans.push(scanRequest);
              this.saveOfflineScansToStorage(); // PERSIST TO STORAGE
              return of(null as any);
            })
          );
        })
      ).subscribe((response: ScanWithStatusResult | CartonScanResponse) => {
        if (this.isScanWithStatusResult(response)) {
          this.handleEnhancedScanResponse(response);
        } else if (response) {
          this.handleApiScanResponse(response);
        }
      });
    } else {
      // Offline mode - process locally and queue for sync
      this.processCartonLocally(cartonNo);
      this.offlineScans.push(scanRequest);
      this.saveOfflineScansToStorage(); // PERSIST TO STORAGE
      this.showOfflineIndicator = true;
    }
  }

  /**
   * Enhanced scan response handler with order status integration
   */
  private handleEnhancedScanResponse(response: ScanWithStatusResult): void {
    if (response.success) {
      // Create local scan item for UI
      const scanItem: CartonScanItem = {
        cartonNo: response.cartonNo,
        scannedAt: new Date(),
        isExpected: response.status === 'matched' || response.status === 'arrived',
        orderId: this.currentSession?.orderId,
        status: response.status as 'scanned' | 'matched' | 'unexpected'
      };

      this.addScanItemToHistory(scanItem);

      // Update progress with enhanced data
      this.updateProgressFromEnhancedResponse(response);

      // Handle order status updates
      if (response.orderStatusUpdated || response.preAlertStatusUpdated) {
        this.handleStatusUpdates(response);
      }

      // Show enhanced success alert
      this.showEnhancedSuccessAlert(response);

      // Update analytics
      this.updateScanningAnalytics(response);

      // === CARTON LIST UPDATE ===
      // Update carton list with own scan
      this.updateCartonListFromOwnScan(response.cartonNo, response);

      // Emit events
      this.cartonScanned.emit(scanItem);
      this.progressChanged.emit(this.progress);

      // Check for workflow advancement
      if (response.impactAnalysis.ordersAffected > 0) {
        this.checkWorkflowAdvancement();
      }

    } else {
      this.showAlertMessage(response.message || 'Enhanced scan failed', 'error');
      this.playErrorSound();
      this.vibrate([200, 100, 200]);
    }
  }

  /**
   * Type guard to distinguish between enhanced and basic scan responses
   */
  private isScanWithStatusResult(response: ScanWithStatusResult | CartonScanResponse): response is ScanWithStatusResult {
    return 'orderStatusUpdated' in response && 'completionProgress' in response;
  }

  private handleApiScanResponse(response: CartonScanResponse): void {
    if (response.success) {
      // Update local state with server response
      this.currentSession = response.session;
      this.updateProgressFromSession(response.session);
      
      // Create local scan item for UI
      const scanItem: CartonScanItem = {
        cartonNo: response.cartonNo,
        scannedAt: new Date(),
        isExpected: response.isExpected,
        orderId: this.currentSession.orderId,
        status: response.status as 'scanned' | 'matched' | 'unexpected'
      };
      
      this.addScanItemToHistory(scanItem);
      this.showSuccessAlert(response);
      
      // Emit events
      this.cartonScanned.emit(scanItem);
      this.progressChanged.emit(this.progress);
      
    } else {
      this.showAlertMessage(response.message || 'Scan failed', 'error');
      this.playErrorSound();
      this.vibrate([200, 100, 200]);
    }
  }

  private processCartonLocally(cartonNo: string): void {
    // Use same matching logic as handleBarcodeScan (check currentCartonList first for auto-detected pre-alerts)
    const isExpected = (this.currentCartonList?.cartons.some(c => c.cartonNumber === cartonNo)) ||
                      this.config.expectedCartons.includes(cartonNo) ||
                      (this.autoDetectedPreAlert && this.isCartonInDetectedPreAlert(cartonNo));

    // Create scan item
    const scanItem: CartonScanItem = {
      cartonNo,
      scannedAt: new Date(),
      isExpected,
      orderId: this.orderId,
      status: isExpected ? 'matched' : 'unexpected'
    };

    this.addScanItemToHistory(scanItem);
    this.updateProgress();

    // **SESSION PROTECTION: Save session data after every scan**
    this.saveSessionDataToStorage();

    // Emit events
    this.cartonScanned.emit(scanItem);
    this.progressChanged.emit(this.progress);

    // Show success message
    const message = isExpected
      ? `✓ Carton ${cartonNo} matched!`
      : `⚠ Unexpected carton ${cartonNo} added`;

    this.showAlertMessage(message, isExpected ? 'success' : 'warning');
    this.playSuccessSound(isExpected);
    this.vibrate(isExpected ? [100] : [100, 50, 100]);

    // Check completion
    if (this.progress.isComplete && this.config.autoCompleteOnMatch) {
      this.completeScan();
    }

    // Detect shortages
    if (this.progress.shortageCount > 0) {
      const missingCartons = this.getMissingCartons();
      this.shortageDetected.emit(missingCartons);
    }
  }

  addManualEntry(): void {
    if (!this.manualCartonNo.trim()) return;

    this.triggerHapticFeedback('button');
    this.processCartonScan(this.manualCartonNo.trim());
    this.manualCartonNo = '';
    this.manualEntry = false;
  }

  removeScannedCarton(cartonNo: string): void {
    const index = this.scannedCartons.findIndex(item => item.cartonNo === cartonNo);
    if (index > -1) {
      this.scannedCartons.splice(index, 1);
      this.updateProgress();
      this.progressChanged.emit(this.progress);
    }
  }

  clearAll(): void {
    this.triggerHapticFeedback('button');
    this.scannedCartons = [];
    this.updateProgress();
    this.progressChanged.emit(this.progress);
    this.showAlertMessage('All scanned cartons cleared', 'success');
  }

  completeScan(): void {
    this.triggerHapticFeedback('complete');

    if (this.enableSessionManagement && this.sessionActive && this.currentSession) {
      this.completeScanSession();
    } else {
      this.scanCompleted.emit([...this.scannedCartons]);
      this.showAlertMessage('Scan completed successfully!', 'success');
    }
  }

  // === SESSION MANAGEMENT METHODS ===
  
  async startScanSession(orderId?: string): Promise<void> {
    if (this.sessionActive) {
      this.showAlertMessage('Session already active', 'warning');
      return;
    }

    try {
      const startRequest: StartSessionRequest = {
        orderId: orderId || this.orderId || this.sessionConfig.orderId,
        sessionType: this.sessionConfig.sessionType,
        expectedCartons: this.config.expectedCartons,
        scannedBy: this.authService.getCurrentUser()?.username || 'mobile_user',
        location: 'mobile_scan'
      };

      const session = await this.mobileScanningService.startScanSession(startRequest).toPromise();
      if (session) {
        this.currentSession = session;
        this.sessionActive = true;
        this.sessionStartTime = new Date();
        this.showSessionControls = true;
        this.sessionStarted.emit(session);
        this.showAlertMessage('Scan session started successfully!', 'success');
        this.playSuccessSound(true);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start session';
      this.showAlertMessage(errorMessage, 'error');
      this.scanError.emit(errorMessage);

      // Log error to backend for remote debugging
      this.logErrorToBackend('startScanSession', error, {
        orderId: orderId || this.orderId || this.sessionConfig.orderId,
        sessionType: this.sessionConfig.sessionType
      });
    }
  }

  async completeScanSession(): Promise<void> {
    if (!this.currentSession) return;

    try {
      const summary = await this.mobileScanningService.completeScanSession(
        this.currentSession.sessionId,
        'Mobile scan completed'
      ).toPromise();

      if (summary) {
        this.sessionActive = false;
        this.sessionPaused = false;
        this.showSessionControls = false;
        this.sessionCompleted.emit(summary);
        this.showAlertMessage('Session completed successfully!', 'success');
        this.playSuccessSound(true);
        
        // Clear current session
        this.currentSession = null;
        this.sessionStartTime = null;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete session';
      this.showAlertMessage(errorMessage, 'error');
      this.scanError.emit(errorMessage);

      // Log error to backend for remote debugging
      this.logErrorToBackend('completeScanSession', error);
    }
  }

  async pauseScanSession(): Promise<void> {
    if (!this.currentSession || !this.sessionActive) return;

    try {
      const session = await this.mobileScanningService.pauseScanSession(
        this.currentSession.sessionId
      ).toPromise();

      if (session) {
        this.sessionPaused = true;
        this.showAlertMessage('Session paused', 'success');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to pause session';
      this.showAlertMessage(errorMessage, 'error');
    }
  }

  async resumeScanSession(): Promise<void> {
    if (!this.currentSession || !this.sessionPaused) return;

    try {
      const session = await this.mobileScanningService.resumeScanSession(
        this.currentSession.sessionId
      ).toPromise();

      if (session) {
        this.sessionPaused = false;
        this.showAlertMessage('Session resumed', 'success');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resume session';
      this.showAlertMessage(errorMessage, 'error');
    }
  }

  // === BULK UPDATE METHODS ===

  /**
   * Complete session with bulk status update
   * Updates all scanned cartons in a single atomic transaction
   * Implements retry logic and keeps session open on partial failure
   */
  async completeWithBulkUpdate(): Promise<void> {
    if (!this.currentSession || !this.currentCartonList) {
      this.showAlertMessage('No active session or carton list', 'error');
      return;
    }

    try {
      // **PERFORMANCE FIX: Flush any pending batch updates first**
      console.log('[Complete] Flushing pending batch updates before bulk update...');
      await this.forceBatchUpdateFlush();

      this.bulkUpdateInProgress = true;
      this.showBulkUpdateResults = false;

      // **CRITICAL FIX: Collect ALL scanned cartons from ALL sources**
      // This fixes the issue where offline scans were excluded from bulk update
      const allScannedCartons = new Set<string>();

      // 1. Add cartons from main scanned list
      this.scannedCartons.forEach(item => {
        allScannedCartons.add(item.cartonNo);
      });

      // 2. Add cartons from offline pending scans (network failures)
      this.offlineScans.forEach(scan => {
        allScannedCartons.add(scan.cartonNo);
      });

      // 3. Add cartons marked as scanned in carton list (if available)
      if (this.currentCartonList && this.scannedCartonsFromList.length > 0) {
        this.scannedCartonsFromList.forEach(c => {
          allScannedCartons.add(c.cartonNumber);
        });
      }

      // Convert Set to Array (deduplicates automatically)
      const cartonNumbersToUpdate = Array.from(allScannedCartons);

      if (cartonNumbersToUpdate.length === 0) {
        this.showAlertMessage('No cartons scanned yet', 'warning');
        this.bulkUpdateInProgress = false;
        return;
      }

      console.log('[Complete] Updating ALL scanned cartons:', {
        totalToUpdate: cartonNumbersToUpdate.length,
        fromScannedList: this.scannedCartons.length,
        fromOfflineQueue: this.offlineScans.length,
        fromCartonList: this.scannedCartonsFromList.length,
        cartonNumbers: cartonNumbersToUpdate
      });

      // Show user what's happening
      this.showAlertMessage(
        `⏳ Updating ${cartonNumbersToUpdate.length} scanned cartons in database...`,
        'warning'
      );

      // Call bulk update API with ALL cartons
      const result = await this.mobileScanningService.bulkUpdateCartonStatus(
        cartonNumbersToUpdate, // ALL SCANNED CARTONS (not just scannedCartonsFromList)
        this.currentSession.sessionId,
        this.currentCartonList.ladingBill
      ).toPromise();

      if (result) {
        this.bulkUpdateResults = result;
        this.showBulkUpdateResults = true;

        console.log('Bulk update results:', result);

        // Show success message
        if (result.allSuccessful) {
          this.showAlertMessage(
            `✅ Success! ${result.successfulUpdates} carton(s) updated` +
            (result.skippedAlreadyScanned > 0 ? ` (${result.skippedAlreadyScanned} already scanned)` : ''),
            'success'
          );

          // **CLEAR OFFLINE SCANS AFTER SUCCESSFUL UPDATE**
          this.offlineScans = [];
          this.clearOfflineScansFromStorage();

          // All successful - close session
          this.playSuccessSound(true);
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });

          // Complete the session
          await this.completeScanSession();
        } else {
          // Partial failure - keep session open
          this.showAlertMessage(
            `⚠️ Partial Update: ${result.successfulUpdates} successful, ${result.failedUpdates} failed. ` +
            `Press Complete again to retry failed cartons.`,
            'warning'
          );

          this.playErrorSound();

          // **REMOVE SUCCESSFUL CARTONS FROM OFFLINE QUEUE**
          if (result.successfulUpdates > 0 && this.offlineScans.length > 0) {
            const failedCartonNumbers = new Set(result.failedCartons?.map(f => f.cartonNumber) || []);
            this.offlineScans = this.offlineScans.filter(scan =>
              failedCartonNumbers.has(scan.cartonNo)
            );
            this.saveOfflineScansToStorage();
          }

          // Reload carton list to reflect updates
          if (this.currentCartonList) {
            await this.reloadCartonListAfterBulkUpdate(this.currentCartonList.ladingBill);
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete bulk update';
      console.error('Bulk update error:', error);

      this.showAlertMessage(`❌ Error: ${errorMessage}`, 'error');
      this.scanError.emit(errorMessage);
      this.playErrorSound();

      // Log error to backend for remote debugging
      this.logErrorToBackend('completeWithBulkUpdate', error, {
        totalCartons: this.scannedCartons.length + this.offlineScans.length,
        offlineScans: this.offlineScans.length
      });
    } finally {
      this.bulkUpdateInProgress = false;
    }
  }

  /**
   * Reload carton list after bulk update to reflect updated status
   */
  private async reloadCartonListAfterBulkUpdate(ladingBill: string): Promise<void> {
    try {
      const cartonList = await this.mobileScanningService.getPreAlertCartonList(ladingBill).toPromise();
      if (cartonList) {
        this.currentCartonList = cartonList;
        console.log(`Carton list reloaded: ${cartonList.scannedCount}/${cartonList.totalCartons} scanned`);
      }
    } catch (error) {
      console.error('Failed to reload carton list:', error);
    }
  }

  /**
   * Close bulk update results modal
   */
  closeBulkUpdateResults(): void {
    this.showBulkUpdateResults = false;
    this.bulkUpdateResults = null;
  }

  // === ERROR LOGGING TO BACKEND ===

  /**
   * Log error to backend error logging system for remote debugging
   * Enables admins to see mobile scanner errors in System Health without VPS access
   */
  private logErrorToBackend(source: string, error: any, additionalContext?: any): void {
    const errorRequest = {
      source: `MobileCartonScanner.${source}`,
      message: error instanceof Error ? error.message : String(error),
      stackTrace: error instanceof Error ? error.stack : undefined,
      additionalData: JSON.stringify({
        sessionId: this.currentSession?.sessionId,
        preAlert: this.currentCartonList?.ladingBill,
        offlineScansPending: this.offlineScans.length,
        totalScanned: this.scannedCartons.length,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...additionalContext
      })
    };

    // Fire and forget - don't wait for response to avoid blocking UI
    this.errorLogService.logFrontendError(errorRequest).subscribe({
      next: () => console.log('[Mobile Scanner] Error logged to backend for remote debugging'),
      error: (logErr) => console.error('[Mobile Scanner] Failed to log error to backend:', logErr)
    });
  }

  // === COPY TO CLIPBOARD METHODS ===

  /**
   * Copy carton list to clipboard
   * Format: Pre-Alert: ABC123\nScanned Cartons:\nCTN1\nCTN2\n...
   */
  copyToClipboard(listType: 'all' | 'scanned' | 'remaining'): void {
    if (!this.currentCartonList) {
      this.showAlertMessage('No carton list available', 'error');
      return;
    }

    try {
      // Get the appropriate carton list
      let cartons: CartonListItem[] = [];
      let listTitle = '';

      switch (listType) {
        case 'all':
          cartons = this.allCartons;
          listTitle = 'All Cartons';
          break;
        case 'scanned':
          cartons = this.scannedCartonsFromList;
          listTitle = 'Scanned Cartons';
          break;
        case 'remaining':
          cartons = this.unscannedCartons;
          listTitle = 'Remaining Cartons';
          break;
      }

      if (cartons.length === 0) {
        this.showAlertMessage(`No cartons in ${listTitle.toLowerCase()} list`, 'warning');
        return;
      }

      // Format text for clipboard
      const header = `Pre-Alert: ${this.currentCartonList.ladingBill}\n${listTitle}:\n`;
      const cartonNumbers = cartons.map(c => c.cartonNumber).join('\n');
      const clipboardText = header + cartonNumbers;

      // Copy to clipboard using modern Clipboard API with fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(clipboardText).then(() => {
          this.showAlertMessage(
            `✅ Copied ${cartons.length} carton(s) to clipboard`,
            'success'
          );
        }).catch(err => {
          console.error('Failed to copy to clipboard:', err);
          this.fallbackCopyToClipboard(clipboardText);
        });
      } else {
        // Fallback for older browsers
        this.fallbackCopyToClipboard(clipboardText);
      }
    } catch (error) {
      console.error('Copy to clipboard error:', error);
      this.showAlertMessage('Failed to copy to clipboard', 'error');
    }
  }

  /**
   * Fallback copy method for older browsers
   */
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showAlertMessage('✅ Copied to clipboard', 'success');
      } else {
        this.showAlertMessage('Failed to copy to clipboard', 'error');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      this.showAlertMessage('Failed to copy to clipboard', 'error');
    }

    document.body.removeChild(textArea);
  }

  private updateProgress(): void {
    const scannedCount = this.scannedCartons.length;
    const expectedCount = this.config.expectedCartons.length;
    const matchedCount = this.scannedCartons.filter(item => item.isExpected).length;
    const unexpectedCount = this.scannedCartons.filter(item => !item.isExpected).length;
    const shortageCount = Math.max(0, expectedCount - matchedCount);
    
    const completionPercentage = expectedCount > 0 
      ? Math.round((matchedCount / expectedCount) * 100)
      : 0;

    const isComplete = expectedCount > 0 && matchedCount >= expectedCount && shortageCount === 0;

    this.progress = {
      scannedCount,
      expectedCount,
      matchedCount,
      unexpectedCount,
      shortageCount,
      isComplete,
      completionPercentage
    };
  }

  private getMissingCartons(): string[] {
    const scannedCartonNos = this.scannedCartons
      .filter(item => item.isExpected)
      .map(item => item.cartonNo);
    
    return this.config.expectedCartons.filter(cartonNo => 
      !scannedCartonNos.includes(cartonNo)
    );
  }

  // === INITIALIZATION AND CLEANUP ===

  private initializeSessionManagement(): void {
    // Subscribe to current session changes
    this.currentSession$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(session => {
      this.currentSession = session;
      if (session) {
        this.sessionActive = session.status === 'active';
        this.sessionPaused = session.status === 'paused';
        this.updateProgressFromSession(session);
      }
    });

    // Check if there's an existing session
    const existingSession = this.mobileScanningService.getCurrentSession();
    if (existingSession && existingSession.status === 'active') {
      this.currentSession = existingSession;
      this.sessionActive = true;
      this.showSessionControls = true;
    }
  }

  private setupAutoSave(): void {
    if (this.sessionConfig.autoSaveInterval > 0) {
      this.autoSaveTimer = setInterval(() => {
        this.autoSaveProgress();
      }, this.sessionConfig.autoSaveInterval);
    }
  }

  private cleanupSession(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
    
    // Attempt to sync any offline scans
    if (this.offlineScans.length > 0) {
      this.syncOfflineScans();
    }
  }

  private autoSaveProgress(): void {
    if (this.sessionActive && this.currentSession) {
      // Save current progress to local storage as backup
      try {
        const progressData = {
          sessionId: this.currentSession.sessionId,
          scannedCartons: this.scannedCartons,
          progress: this.progress,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('mobile_scan_progress', JSON.stringify(progressData));
      } catch (error) {
        console.warn('Failed to auto-save progress:', error);
      }
    }
  }

  // === AUDIO AND HAPTIC FEEDBACK ===

  private playSuccessSound(isExpected: boolean): void {
    if (!this.sessionConfig.audioFeedback) return;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies for expected vs unexpected
      oscillator.frequency.value = isExpected ? 880 : 660;
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (error) {
      console.warn('Could not play success sound:', error);
    }
  }

  private playErrorSound(): void {
    if (!this.sessionConfig.audioFeedback) return;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 300; // Lower frequency for errors
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Could not play error sound:', error);
    }
  }

  // === PHASE 1: GENERIC SOUND METHOD FOR FAB ===
  private playSound(type: 'start' | 'stop' | 'success' | 'error'): void {
    if (!this.soundEnabled) return;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different sound for different actions
      switch (type) {
        case 'start':
          oscillator.frequency.value = 880; // High beep for start
          gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);
          break;
        case 'stop':
          oscillator.frequency.value = 440; // Lower beep for stop
          gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);
          break;
        case 'success':
          this.playSuccessSound(true);
          break;
        case 'error':
          this.playErrorSound();
          break;
      }
    } catch (error) {
      console.warn('Could not play sound:', error);
    }
  }

  private vibrate(pattern: number[]): void {
    if (!this.sessionConfig.vibrationFeedback || !navigator.vibrate) return;

    try {
      navigator.vibrate(pattern);
    } catch (error) {
      console.warn('Could not vibrate:', error);
    }
  }

  // === DATA MANAGEMENT ===

  private addScanItemToHistory(scanItem: CartonScanItem): void {
    this.scannedCartons.unshift(scanItem);

    // Limit history dynamically based on pre-alert total, or use maxHistory as fallback
    // This ensures the display can accommodate all cartons in the pre-alert
    const effectiveMax = this.currentCartonList?.totalCartons || this.maxHistory;
    if (this.scannedCartons.length > effectiveMax) {
      this.scannedCartons = this.scannedCartons.slice(0, effectiveMax);
    }
  }

  private updateProgressFromSession(session: ScanSession): void {
    this.progress = {
      scannedCount: session.scannedCartons.length,
      expectedCount: session.expectedCartons.length,
      matchedCount: session.progress.matchedCount,
      unexpectedCount: session.progress.unexpectedCount,
      shortageCount: session.progress.shortageCount,
      isComplete: session.progress.completionPercentage >= 100,
      completionPercentage: session.progress.completionPercentage
    };
  }

  private showSuccessAlert(response: CartonScanResponse): void {
    let message = '';
    switch (response.status) {
      case 'matched':
        message = `✓ Carton ${response.cartonNo} matched!`;
        break;
      case 'arrived':
        message = `✓ Carton ${response.cartonNo} arrived and scanned!`;
        break;
      case 'unexpected':
        message = `⚠ Unexpected carton ${response.cartonNo} added`;
        break;
      case 'duplicate':
        message = `⚠ Carton ${response.cartonNo} already scanned`;
        break;
      default:
        message = response.message || 'Carton processed';
    }

    const alertType: 'success' | 'warning' | 'error' =
      (response.status === 'matched' || response.status === 'arrived') ? 'success' :
      response.status === 'unexpected' ? 'warning' : 'error';

    this.showAlertMessage(message, alertType);
  }

  // === OFFLINE SYNC ===

  private async syncOfflineScans(): Promise<void> {
    if (this.offlineScans.length === 0 || this.syncInProgress) return;

    this.syncInProgress = true;
    let syncedCount = 0;

    try {
      for (const scanRequest of this.offlineScans) {
        try {
          await this.mobileScanningService.scanCarton(scanRequest).toPromise();
          syncedCount++;
        } catch (error) {
          console.warn('Failed to sync scan:', scanRequest.cartonNo, error);
          // Keep failed scans for retry
          break;
        }
      }

      // Remove synced scans
      this.offlineScans.splice(0, syncedCount);

      if (this.offlineScans.length === 0) {
        this.showOfflineIndicator = false;
        this.apiConnected = true;
        this.clearOfflineScansFromStorage(); // CLEAR STORAGE AFTER SUCCESSFUL SYNC
        this.showAlertMessage(`✅ Synced ${syncedCount} offline scans successfully`, 'success');
      } else {
        // Still have pending scans, update storage
        this.saveOfflineScansToStorage();
        this.showAlertMessage(`⚠ Synced ${syncedCount} scans, ${this.offlineScans.length} still pending`, 'warning');
      }

    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  retrySyncOfflineScans(): void {
    if (this.offlineScans.length > 0) {
      this.syncOfflineScans();
    }
  }

  // === SESSION PROTECTION: PREVENT DATA LOSS ===

  /**
   * Prevent accidental navigation away from scanner page
   * Shows confirmation dialog if user has unsaved scans
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.scannedCartons.length > 0 || this.pendingStatusUpdates.length > 0) {
      const confirmationMessage = `You have ${this.scannedCartons.length} scanned cartons. Are you sure you want to leave?`;
      $event.returnValue = confirmationMessage;
      // Modern browsers will show a generic confirmation dialog
    }
  }

  /**
   * Save session data on EVERY scan (not just when session active)
   * This ensures data persists even if user navigates away or app crashes
   */
  private saveSessionDataToStorage(): void {
    try {
      const sessionData = {
        scannedCartons: this.scannedCartons,
        pendingStatusUpdates: this.pendingStatusUpdates,
        currentSession: this.currentSession,
        currentCartonList: this.currentCartonList,
        progress: this.progress,
        timestamp: new Date().toISOString(),
        sessionId: this.currentSession?.sessionId
      };
      localStorage.setItem('rarr_mobile_scanner_session', JSON.stringify(sessionData));
      console.log(`[Session Protection] Saved ${this.scannedCartons.length} cartons to storage`);
    } catch (error) {
      console.error('[Session Protection] Failed to save session data:', error);
    }
  }

  /**
   * Restore session data from storage on component init
   * Automatically recovers scanning session after page refresh or navigation
   */
  private restoreSessionFromStorage(): void {
    try {
      const stored = localStorage.getItem('rarr_mobile_scanner_session');
      if (stored) {
        const data = JSON.parse(stored);
        const age = Date.now() - new Date(data.timestamp).getTime();

        // Only restore if less than 8 hours old
        if (age < 8 * 60 * 60 * 1000) {
          this.scannedCartons = data.scannedCartons || [];
          this.pendingStatusUpdates = data.pendingStatusUpdates || [];
          this.currentSession = data.currentSession;
          this.currentCartonList = data.currentCartonList;
          this.progress = data.progress || this.progress;

          console.log(`[Session Protection] Restored session with ${this.scannedCartons.length} cartons`);

          // Show restoration notification
          if (this.scannedCartons.length > 0) {
            this.showAlertMessage(
              `✅ Session restored: ${this.scannedCartons.length} scanned cartons recovered`,
              'success'
            );
          }

          // Update progress UI
          this.updateProgress();
        } else {
          console.log('[Session Protection] Session too old, discarding');
          localStorage.removeItem('rarr_mobile_scanner_session');
        }
      }
    } catch (error) {
      console.error('[Session Protection] Failed to restore session:', error);
      localStorage.removeItem('rarr_mobile_scanner_session');
    }
  }

  /**
   * Clear session data from storage (called after successful completion)
   */
  private clearSessionFromLocalStorage(): void {
    try {
      localStorage.removeItem('rarr_mobile_scanner_session');
      console.log('[Session Protection] Session data cleared from storage');
    } catch (error) {
      console.error('[Session Protection] Failed to clear session data:', error);
    }
  }

  // === OFFLINE PERSISTENCE: STORAGE AND NETWORK DETECTION ===

  /**
   * Save offline scans to localStorage for persistence across page reloads
   */
  private saveOfflineScansToStorage(): void {
    try {
      const data = {
        scans: this.offlineScans,
        timestamp: new Date().toISOString(),
        sessionId: this.currentSession?.sessionId,
        version: '1.0'
      };
      localStorage.setItem('rarr_pending_scans', JSON.stringify(data));
      console.log(`Saved ${this.offlineScans.length} offline scans to localStorage`);
    } catch (error) {
      console.error('Failed to save offline scans to storage:', error);
      // If storage fails (quota exceeded), alert user
      this.showAlertMessage('⚠ Storage full - some scans may be lost if app closes', 'warning');
    }
  }

  /**
   * Load offline scans from localStorage on component initialization
   */
  private loadOfflineScansFromStorage(): void {
    try {
      const stored = localStorage.getItem('rarr_pending_scans');
      if (stored) {
        const data = JSON.parse(stored);

        // Check if data is recent (within last 7 days to avoid stale data)
        const timestamp = new Date(data.timestamp);
        const age = Date.now() - timestamp.getTime();
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days

        if (age < maxAge && Array.isArray(data.scans) && data.scans.length > 0) {
          this.offlineScans = data.scans;
          this.showOfflineIndicator = true;
          console.log(`Restored ${this.offlineScans.length} offline scans from storage (saved ${Math.round(age / 60000)} minutes ago)`);

          // Show notification to user
          this.showAlertMessage(
            `📦 Restored ${this.offlineScans.length} unsynced scans from previous session`,
            'warning'
          );

          // Auto-attempt sync if online
          if (navigator.onLine && this.apiConnected) {
            setTimeout(() => {
              console.log('Auto-syncing restored scans...');
              this.syncOfflineScans();
            }, 2000); // Wait 2s after init
          }
        } else if (age >= maxAge) {
          console.warn('Offline scans too old, discarding:', data.scans.length);
          localStorage.removeItem('rarr_pending_scans');
        }
      }
    } catch (error) {
      console.error('Failed to load offline scans from storage:', error);
      // Clear corrupted data
      localStorage.removeItem('rarr_pending_scans');
    }
  }

  /**
   * Setup network connectivity listeners for automatic sync
   */
  private setupNetworkListener(): void {
    // Listen for online event (network restored)
    window.addEventListener('online', () => {
      console.log('✅ Network connection restored');
      this.apiConnected = true;

      // Attempt to sync pending offline scans
      if (this.offlineScans.length > 0) {
        this.showAlertMessage(`🔄 Network restored - syncing ${this.offlineScans.length} pending scans...`, 'success');
        setTimeout(() => {
          this.syncOfflineScans();
        }, 1000); // Small delay to ensure connection is stable
      } else {
        this.showOfflineIndicator = false;
      }
    });

    // Listen for offline event (network lost)
    window.addEventListener('offline', () => {
      console.warn('⚠ Network connection lost');
      this.apiConnected = false;
      this.showOfflineIndicator = true;
      this.showAlertMessage('📵 No internet - scans will be queued and synced when online', 'warning');
    });

    // Initial network status check
    this.apiConnected = navigator.onLine;
    console.log('Initial network status:', navigator.onLine ? 'Online' : 'Offline');
  }

  /**
   * Clear offline scans from storage after successful sync
   */
  private clearOfflineScansFromStorage(): void {
    try {
      localStorage.removeItem('rarr_pending_scans');
      console.log('Cleared offline scans from storage');
    } catch (error) {
      console.error('Failed to clear offline scans from storage:', error);
    }
  }

  private showAlertMessage(message: string, type: 'success' | 'warning' | 'error'): void {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.showAlert = false;
    }, 3000);
  }

  // === DISPLAY AND UTILITY METHODS ===

  displayCartonStatus(): string {
    const scanned = this.progress.scannedCount;
    const expected = this.progress.expectedCount;
    const matched = this.progress.matchedCount;
    const percentage = this.progress.completionPercentage;
    
    let status = `Scanned ${scanned} of ${expected} cartons (${matched} matched) - ${percentage}%`;
    
    if (this.progress.shortageCount > 0) {
      status += ` | Missing ${this.progress.shortageCount} cartons`;
    }
    
    if (this.progress.unexpectedCount > 0) {
      status += ` | ${this.progress.unexpectedCount} unexpected`;
    }
    
    return status;
  }

  getSessionDuration(): string {
    if (!this.sessionStartTime) return '00:00:00';
    
    const now = new Date();
    const diffMs = now.getTime() - this.sessionStartTime.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    return `${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')}`;
  }

  // Getters for template
  get hasShortage(): boolean {
    return this.progress.shortageCount > 0;
  }

  get missingCartons(): string[] {
    return this.getMissingCartons();
  }

  get canStartSession(): boolean {
    return this.enableSessionManagement && 
           !this.sessionActive && 
           !this.disabled && 
           this.config.expectedCartons.length > 0;
  }

  get canCompleteSession(): boolean {
    return this.sessionActive && 
           !!this.currentSession && 
           this.progress.scannedCount > 0;
  }

  get sessionStatusText(): string {
    if (!this.sessionActive) return 'No active session';
    if (this.sessionPaused) return 'Session paused';
    return 'Session active';
  }
  
  get cameraDeviceId(): string | undefined {
    return this.selectedDevice?.deviceId;
  }
  
  get canUseCamera(): boolean {
    return this.cameraEnabled && this.hasDevicePermission && !this.disabled;
  }
  
  get cameraButtonText(): string {
    return this.cameraScannerActive ? 'Stop Camera' : 'Start Camera';
  }
  
  get cameraButtonClass(): string {
    return this.cameraScannerActive ? 'btn-danger' : 'btn-primary';
  }

  get offlineScansCount(): number {
    return this.offlineScans.length;
  }

  /**
   * Get total unique scanned cartons across all sources
   * Fixes the issue where counter was capped at 100 or didn't include offline scans
   */
  get totalScannedCount(): number {
    const uniqueCartons = new Set<string>();

    // Add from main scanned list
    this.scannedCartons.forEach(item => uniqueCartons.add(item.cartonNo));

    // Add from offline pending
    this.offlineScans.forEach(scan => uniqueCartons.add(scan.cartonNo));

    return uniqueCartons.size;
  }

  /**
   * Get display status for counter button
   * Shows accurate count with visual indicator for offline scans
   */
  get scanCountStatus(): { count: number; label: string; icon: string; color: string } {
    const total = this.totalScannedCount;
    const offline = this.offlineScans.length;

    if (offline > 0) {
      return {
        count: total,
        label: `${offline} pending`,
        icon: 'bi-cloud-upload',
        color: 'warning'
      };
    }

    return {
      count: total,
      label: 'scanned',
      icon: 'bi-list',
      color: 'primary'
    };
  }

  get progressBarClass(): string {
    if (this.progress.completionPercentage >= 100) return 'bg-success';
    if (this.progress.completionPercentage >= 75) return 'bg-info';
    if (this.progress.completionPercentage >= 50) return 'bg-warning';
    return 'bg-danger';
  }

  get alertClass(): string {
    const baseClass = 'alert alert-dismissible';
    switch (this.alertType) {
      case 'success': return `${baseClass} alert-success`;
      case 'warning': return `${baseClass} alert-warning`;
      case 'error': return `${baseClass} alert-danger`;
      default: return `${baseClass} alert-info`;
    }
  }

  async toggleCameraScanner(): Promise<void> {
    if (this.sessionPaused) {
      this.showAlertMessage('Cannot start camera scanner while session is paused', 'warning');
      return;
    }

    if (this.cameraScannerActive) {
      await this.stopCameraScanner();
    } else {
      this.startCameraScanner();
    }
  }

  // === PHASE 1: FAB BUTTON TOGGLE ===

  async toggleFAB(): Promise<void> {
    if (this.disabled || this.sessionPaused) {
      this.showAlertMessage('Scanner is currently unavailable', 'warning');
      return;
    }

    // If camera scanner is active, stop it
    if (this.cameraScannerActive) {
      await this.stopCameraScanner();
      this.triggerHapticFeedback('button');
      this.showAlertMessage('Scanner stopped', 'success');
    } else {
      // Start camera scanner
      this.startCameraScanner();
      this.triggerHapticFeedback('scanStart');
      this.playSound('start');
    }
  }

  // === PHASE 1: COLLAPSIBLE PANEL TOGGLES ===

  toggleConfigPanel(): void {
    this.showConfigPanel = !this.showConfigPanel;
    this.triggerHapticFeedback('button');
  }

  // === PHASE 1: ENHANCED HAPTIC FEEDBACK PATTERNS ===

  private triggerHapticFeedback(type: 'scanStart' | 'success' | 'error' | 'warning' | 'complete' | 'button'): void {
    if (!('vibrate' in navigator) || !this.hapticEnabled) return;

    const patterns: Record<string, number | number[]> = {
      scanStart: [30],
      success: [50, 50, 50],
      error: [200, 100, 50, 50, 50],
      warning: [100, 100, 100],
      complete: [50, 50, 100, 50, 150],
      button: [10]
    };

    const pattern = patterns[type];
    if (pattern) {
      navigator.vibrate(pattern);
    }
  }
  
  async initializeCameraDevices(): Promise<void> {
    try {
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.warn('Camera not supported on this device');
        return;
      }
      
      // Request permission and get devices
      let stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.hasDevicePermission = true;

      // Stop the stream immediately, we just needed permission
      // Android 15: Properly cleanup stream to avoid conflicts
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      await new Promise(resolve => setTimeout(resolve, 300)); // Wait for cleanup
      stream = null as any; // Release reference

      // Get available video devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableDevices = devices.filter(device => device.kind === 'videoinput');
      
      // Select the back camera if available (usually better for barcode scanning)
      this.selectedDevice = this.availableDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('rear')
      ) || this.availableDevices[0];
      
      this.cameraEnabled = this.availableDevices.length > 0;
      
    } catch (error) {
      console.error('Camera initialization failed:', error);
      this.hasDevicePermission = false;
      this.cameraEnabled = false;
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          this.showAlertMessage('Camera permission denied. Please enable camera access to use scanner.', 'warning');
        } else if (error.name === 'NotFoundError') {
          this.showAlertMessage('No camera found on this device.', 'warning');
        } else {
          this.showAlertMessage(`Camera error: ${error.message}`, 'error');
        }
      }
    }
  }

  /**
   * Get platform-specific camera constraints
   * Android 15 requires different settings than iOS
   * CRITICAL: Don't use facingMode and deviceId together - causes black screen
   */
  private getCameraConstraints(): MediaTrackConstraints {
    const isAndroid = /Android/i.test(navigator.userAgent);

    if (this.selectedDevice?.deviceId) {
      // Use specific device (when user has selected a camera or we've detected one)
      console.log(`[Camera Constraints] Using deviceId: ${this.selectedDevice.label || this.selectedDevice.deviceId}`);
      return {
        deviceId: { exact: this.selectedDevice.deviceId },
        width: isAndroid ? { ideal: 1280 } : { min: 640, ideal: 1280, max: 1920 },
        height: isAndroid ? { ideal: 720 } : { min: 480, ideal: 720, max: 1080 },
      };
    } else {
      // Fallback to facingMode (first initialization)
      console.log('[Camera Constraints] Using facingMode: environment (back camera)');
      return {
        facingMode: 'environment',
        width: isAndroid ? { ideal: 1280 } : { min: 640, ideal: 1280, max: 1920 },
        height: isAndroid ? { ideal: 720 } : { min: 480, ideal: 720, max: 1080 },
      };
    }
  }

  private async startCameraScanner(): Promise<void> {
    if (!this.hasDevicePermission || !this.selectedDevice) {
      await this.initializeCameraDevices();

      // Check again after initialization
      if (!this.hasDevicePermission || !this.selectedDevice) {
        console.warn('Camera permission denied or no camera available');
        return;
      }
    }

    this.cameraScannerActive = true;

    // Initialize Tesseract OCR worker for fallback text detection
    this.initializeTesseractWorker();

    // Initialize Quagga2 scanner for enhanced barcode scanning
    // Use setTimeout to ensure DOM has rendered the scanner container
    setTimeout(() => {
      console.log('[Camera Scanner] Checking if Quagga scanner element is ready...');

      if (!this.quaggaScannerElement) {
        console.error('[Camera Scanner] ❌ Quagga scanner element not found! Retrying...');

        // Retry after additional delay
        setTimeout(() => {
          if (this.quaggaScannerElement) {
            console.log('[Camera Scanner] ✅ Quagga scanner element found on retry');
            this.initializeQuagga2Scanner();
          } else {
            console.error('[Camera Scanner] ❌ Quagga scanner element still not found after retry');
          }
        }, 200);
      } else {
        console.log('[Camera Scanner] ✅ Quagga scanner element ready, initializing...');
        this.initializeQuagga2Scanner();
      }
    }, 100);

    // Check if torch is available
    this.checkTorchAvailability();
  }
  
  async stopCameraScanner(): Promise<void> {
    this.cameraScannerActive = false;
    this.torchEnabled = false;

    // Cleanup Quagga2 scanner (Android 15: wait for cleanup to complete)
    await this.cleanupQuagga2();

    // Cleanup Tesseract OCR worker
    this.cleanupTesseractWorker();

    console.log('[Camera Scanner] ✅ Scanner stopped and camera released');
  }
  
  private async checkTorchAvailability(): Promise<void> {
    if (!this.selectedDevice) return;
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: this.selectedDevice.deviceId }
      });
      
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      
      this.torchAvailable = !!(capabilities as any).torch;
      
      // Clean up
      stream.getTracks().forEach(t => t.stop());
      
    } catch (error) {
      console.warn('Could not check torch availability:', error);
      this.torchAvailable = false;
    }
  }
  
  async toggleTorch(): Promise<void> {
    if (!this.torchAvailable) return;
    
    try {
      // Note: This is a simplified implementation
      // The actual torch implementation may need to be handled differently
      // depending on the specific device capabilities and browser support
      this.torchEnabled = !this.torchEnabled;
      this.showAlertMessage(this.torchEnabled ? 'Flash enabled' : 'Flash disabled', 'success');
    } catch (error) {
      console.error('Error toggling torch:', error);
      this.showAlertMessage('Could not toggle flashlight', 'warning');
    }
  }
  
  async selectCamera(device: MediaDeviceInfo): Promise<void> {
    console.log(`[Select Camera] Switching to: ${device.label || device.deviceId}`);
    this.selectedDevice = device;

    // Restart camera scanner if it was active
    if (this.cameraScannerActive) {
      // Wait for cleanup to complete (Android 15 fix)
      await this.stopCameraScanner();

      // Android needs longer delay for camera release
      const delay = /Android/i.test(navigator.userAgent) ? 500 : 100;
      console.log(`[Select Camera] Waiting ${delay}ms for camera release...`);

      setTimeout(() => {
        this.startCameraScanner();
      }, delay);
    }
  }

  /**
   * Switch between available cameras (front/back)
   * Cycles through available video input devices
   */
  switchCamera(): void {
    if (this.availableDevices.length <= 1) {
      console.log('[Camera Switch] Only one camera available');
      return;
    }

    // Find current device index
    const currentIndex = this.availableDevices.findIndex(
      device => device.deviceId === this.selectedDevice?.deviceId
    );

    // Cycle to next camera (wrap around to 0 if at end)
    const nextIndex = (currentIndex + 1) % this.availableDevices.length;
    const nextDevice = this.availableDevices[nextIndex];

    console.log(`[Camera Switch] Switching from camera ${currentIndex} to ${nextIndex}`);
    console.log(`[Camera Switch] New camera: ${nextDevice.label || nextDevice.deviceId}`);

    // Select the next camera (this will restart scanner)
    this.selectCamera(nextDevice);

    // Show feedback to user
    const cameraName = nextDevice.label || (nextIndex === 0 ? 'Back Camera' : 'Front Camera');
    this.showAlertMessage(`Switched to: ${cameraName}`, 'success');
    this.triggerHapticFeedback('button');
  }

  toggleManualEntry(): void {
    this.triggerHapticFeedback('button');
    this.manualEntry = !this.manualEntry;
    if (this.manualEntry) {
      this.manualCartonNo = '';
      // Focus on input after view update
      setTimeout(() => {
        const input = document.getElementById('manualCartonInput') as HTMLInputElement;
        if (input) input.focus();
      }, 100);
    }
  }

  // === ACTION METHODS FOR TEMPLATE ===

  onStartSession(): void {
    if (this.orderId || this.sessionConfig.orderId) {
      this.startScanSession();
    } else {
      this.showAlertMessage('Order ID is required to start session', 'error');
    }
  }

  onCompleteSession(): void {
    if (this.progress.scannedCount === 0) {
      this.showAlertMessage('No cartons scanned yet', 'warning');
      return;
    }
    this.completeScanSession();
  }

  onPauseSession(): void {
    this.pauseScanSession();
  }

  onResumeSession(): void {
    this.resumeScanSession();
  }

  formatTimestamp(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  }

  trackByCartonNo(index: number, item: CartonScanItem): string {
    return item.cartonNo;
  }

  trackByCartonNumber(index: number, item: CartonListItem): string {
    return item.cartonNumber;
  }

  // === NEW ORDER STATUS INTEGRATION METHODS ===

  /**
   * Initialize order status tracking for the current session
   */
  private initializeOrderStatusTracking(): void {
    if (!this.orderId && !this.sessionConfig.orderId) return;

    const orderId = this.orderId || this.sessionConfig.orderId;
    this.loadOrderScanningStatus(orderId);
  }

  /**
   * Load current order scanning status and linked pre-alerts
   */
  private loadOrderScanningStatus(orderId: string): void {
    this.mobileScanningService.getOrderScanningStatus(orderId).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.warn('Could not load order scanning status:', error);
        return of(null);
      })
    ).subscribe(status => {
      if (status) {
        this.currentOrderStatus = status;
        this.loadPreAlertMetrics(status.linkedPreAlerts.map(pa => pa.ladingBill));
      }
    });
  }

  /**
   * Load detailed metrics for linked pre-alerts
   */
  private loadPreAlertMetrics(ladingBills: string[]): void {
    const metricsRequests = ladingBills.map(ladingBill =>
      this.mobileScanningService.getPreAlertScanningMetrics(ladingBill)
    );

    if (metricsRequests.length > 0) {
      combineLatest(metricsRequests).pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.warn('Could not load pre-alert metrics:', error);
          return of([]);
        })
      ).subscribe(metrics => {
        this.preAlertMetrics = metrics;
      });
    }
  }

  /**
   * Setup real-time progress monitoring
   */
  private setupRealTimeProgressMonitoring(): void {
    if (!this.enableRealTimeUpdates) return;

    // Monitor order status changes
    timer(0, this.statusUpdateInterval).pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        if (!this.currentOrderStatus) return of(null);
        return this.mobileScanningService.getOrderScanningStatus(this.currentOrderStatus.orderId);
      }),
      filter(status => status !== null),
      distinctUntilChanged((prev, curr) =>
        prev?.overallProgress.overallCompletionPercentage === curr?.overallProgress.overallCompletionPercentage
      )
    ).subscribe(status => {
      if (status && this.currentOrderStatus) {
        this.handleOrderStatusUpdate(this.currentOrderStatus, status);
        this.currentOrderStatus = status;
      }
    });
  }

  /**
   * Handle order status updates from real-time monitoring
   */
  private handleOrderStatusUpdate(previousStatus: OrderScanningStatus, newStatus: OrderScanningStatus): void {
    if (previousStatus.currentStatus !== newStatus.currentStatus) {
      // Order status changed
      const update: OrderStatusUpdate = {
        orderId: newStatus.orderId,
        previousStatus: previousStatus.currentStatus,
        newStatus: newStatus.currentStatus,
        triggeredBy: 'scanning',
        scanCount: newStatus.overallProgress.overallCompletionPercentage > 0 ? 1 : 0,
        completionPercentage: newStatus.overallProgress.overallCompletionPercentage,
        statusChangeTime: new Date(),
        preAlertStatuses: newStatus.linkedPreAlerts.map(pa => ({
          ladingBill: pa.ladingBill,
          completionPercentage: pa.completionPercentage,
          isComplete: pa.isComplete
        }))
      };

      this.lastOrderStatusUpdate = update;
      this.showStatusTransitionNotification(update);
      this.orderStatusUpdated.emit(update);

      // Update analytics
      this.sessionAnalytics.statusUpdatesTriggered++;

      if (this.isWorkflowAdvancement(previousStatus.currentStatus, newStatus.currentStatus)) {
        this.sessionAnalytics.workflowAdvancementCount++;
        this.workflowAdvanced.emit({
          orderId: newStatus.orderId,
          fromStatus: previousStatus.currentStatus,
          toStatus: newStatus.currentStatus,
          completionPercentage: newStatus.overallProgress.overallCompletionPercentage
        });
      }
    }
  }

  /**
   * Show status transition notification to user
   */
  private showStatusTransitionNotification(update: OrderStatusUpdate): void {
    this.statusTransitionMessage = `Order status updated: ${update.previousStatus} → ${update.newStatus}`;
    this.showStatusTransition = true;
    this.showAlertMessage(this.statusTransitionMessage, 'success');

    // Play success sound for status advancement
    this.playSuccessSound(true);
    this.vibrate([100, 50, 100, 50, 100]);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.showStatusTransition = false;
    }, 5000);
  }

  /**
   * Check if status change represents workflow advancement
   */
  private isWorkflowAdvancement(fromStatus: string, toStatus: string): boolean {
    const statusProgression = [
      'Created', 'Processing', 'CustomsProcessing', 'CustomsCleared',
      'InWarehouse', 'ReadyForDelivery', 'InTransit', 'Delivered', 'Completed'
    ];

    const fromIndex = statusProgression.indexOf(fromStatus);
    const toIndex = statusProgression.indexOf(toStatus);

    return toIndex > fromIndex;
  }

  /**
   * Update progress from enhanced scan response
   */
  private updateProgressFromEnhancedResponse(response: ScanWithStatusResult): void {
    this.progress = {
      ...this.progress,
      scannedCount: response.completionProgress.scannedCount,
      completionPercentage: response.completionProgress.completionPercentage
    };

    // Recalculate other metrics
    this.updateProgress();
  }

  /**
   * Handle status updates triggered by scanning
   */
  private handleStatusUpdates(response: ScanWithStatusResult): void {
    this.orderStatusUpdateInProgress = true;

    // Update UI indicators
    if (response.orderStatusUpdated) {
      this.showAlertMessage(`Order status updated to: ${response.newOrderStatus}`, 'success');
    }

    if (response.preAlertStatusUpdated) {
      this.showAlertMessage('Pre-alert progress updated', 'success');
    }

    // Refresh status data
    if (this.currentOrderStatus) {
      this.loadOrderScanningStatus(this.currentOrderStatus.orderId);
    }

    setTimeout(() => {
      this.orderStatusUpdateInProgress = false;
    }, 2000);
  }

  /**
   * Show enhanced success alert with status information
   */
  private showEnhancedSuccessAlert(response: ScanWithStatusResult): void {
    let message = '';

    switch (response.status) {
      case 'matched':
        message = `✓ Carton ${response.cartonNo} matched!`;
        break;
      case 'unexpected':
        message = `⚠ Unexpected carton ${response.cartonNo} added`;
        break;
      case 'duplicate':
        message = `⚠ Carton ${response.cartonNo} already scanned`;
        break;
      default:
        message = response.message || 'Carton processed';
    }

    // Add status update information
    if (response.orderStatusUpdated || response.preAlertStatusUpdated) {
      message += ' - Status updated!';
    }

    if (response.completionProgress.completionPercentage >= 100) {
      message += ' 🎉 Complete!';
    }

    const alertType: 'success' | 'warning' | 'error' =
      (response.status === 'matched' || response.status === 'arrived') ? 'success' :
      response.status === 'unexpected' ? 'warning' : 'error';

    this.showAlertMessage(message, alertType);
  }

  /**
   * Update scanning analytics
   */
  private updateScanningAnalytics(response: ScanWithStatusResult): void {
    if (!this.lastScanTime) {
      this.lastScanTime = new Date();
      return;
    }

    const now = new Date();
    const timeDiff = (now.getTime() - this.lastScanTime.getTime()) / 1000; // seconds

    // Update scan rate (scans per minute)
    this.sessionAnalytics.scanRate = this.scannedCartons.length /
      ((now.getTime() - (this.sessionStartTime?.getTime() || now.getTime())) / 60000);

    // Update average time per scan
    this.sessionAnalytics.averageTimePerScan =
      (this.sessionAnalytics.averageTimePerScan + timeDiff) / 2;

    this.lastScanTime = now;
  }

  /**
   * Check if order workflow can advance
   */
  private checkWorkflowAdvancement(): void {
    if (!this.currentOrderStatus) return;

    this.mobileScanningService.checkWorkflowAdvancement(this.currentOrderStatus.orderId).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.warn('Workflow advancement check failed:', error);
        return of(null);
      })
    ).subscribe(advancement => {
      if (advancement?.canAdvance) {
        this.showAlertMessage(
          `Order ready to advance to: ${advancement.nextStatus}`,
          'success'
        );
      }
    });
  }

  /**
   * Initialize scanning analytics
   */
  private initializeScanningAnalytics(): void {
    this.sessionAnalytics = {
      scanRate: 0,
      averageTimePerScan: 0,
      statusUpdatesTriggered: 0,
      workflowAdvancementCount: 0
    };
  }

  /**
   * Stop progress monitoring
   */
  private stopProgressMonitoring(): void {
    this.progressMonitoringActive = false;
  }

  /**
   * Save analytics data for reporting
   */
  private saveAnalyticsData(): void {
    if (this.sessionActive && this.currentSession) {
      try {
        const analyticsData = {
          sessionId: this.currentSession.sessionId,
          analytics: this.sessionAnalytics,
          timestamp: new Date().toISOString(),
          orderId: this.currentOrderStatus?.orderId
        };
        localStorage.setItem('mobile_scan_analytics', JSON.stringify(analyticsData));
      } catch (error) {
        console.warn('Failed to save analytics data:', error);
      }
    }
  }

  // === UI GETTERS FOR ENHANCED STATUS DISPLAY ===

  get orderStatusText(): string {
    return this.currentOrderStatus?.currentStatus || 'Unknown';
  }

  get orderCompletionPercentage(): number {
    return this.currentOrderStatus?.overallProgress.overallCompletionPercentage || 0;
  }

  get canOrderAdvance(): boolean {
    return this.currentOrderStatus?.canAdvance || false;
  }

  get nextOrderStatus(): string {
    return this.currentOrderStatus?.nextStatusIfComplete || 'N/A';
  }

  get orderBlockers(): string[] {
    if (!this.currentOrderStatus) return [];
    return this.currentOrderStatus.linkedPreAlerts
      .filter(pa => !pa.isComplete)
      .map(pa => `${pa.ladingBill}: ${pa.completionPercentage}%`);
  }

  get scanningVelocity(): string {
    const rate = this.sessionAnalytics.scanRate;
    if (rate === 0) return 'N/A';
    return `${rate.toFixed(1)} scans/min`;
  }

  get hasStatusUpdates(): boolean {
    return this.sessionAnalytics.statusUpdatesTriggered > 0;
  }

  get workflowAdvancementCount(): number {
    return this.sessionAnalytics.workflowAdvancementCount;
  }

  // === UI ACTION METHODS ===

  toggleOrderStatusPanel(): void {
    this.showOrderStatusPanel = !this.showOrderStatusPanel;
  }

  togglePreAlertProgress(): void {
    this.showPreAlertProgress = !this.showPreAlertProgress;
  }

  toggleScanningAnalytics(): void {
    this.showScanningAnalytics = !this.showScanningAnalytics;
  }

  refreshOrderStatus(): void {
    if (this.currentOrderStatus) {
      this.loadOrderScanningStatus(this.currentOrderStatus.orderId);
    }
  }

  viewWorkflowPreview(): void {
    if (this.currentOrderStatus) {
      this.checkWorkflowAdvancement();
    }
  }

  // === SIGNALR REAL-TIME INTEGRATION METHODS ===

  /**
   * Initialize SignalR subscriptions for real-time scanning events
   */
  private initializeSignalRSubscriptions(): void {
    // Monitor SignalR connection status
    this.signalRService.connectionStatus$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(status => {
      this.signalRConnected = status.isConnected;
      this.signalRReconnecting = status.reconnectAttempts > 0;
    });

    // Subscribe to all scanning activity for situational awareness
    this.signalRService.subscribeToAllScanningEvents().pipe(
      takeUntil(this.destroy$)
    ).subscribe(scanActivity => {
      this.handleRealtimeScanActivity(scanActivity);
    });

    // Subscribe to all order status updates
    this.signalRService.subscribeToAllOrderStatusUpdates().pipe(
      takeUntil(this.destroy$)
    ).subscribe(statusUpdate => {
      this.handleRealtimeOrderStatusUpdate(statusUpdate);
    });

    // Subscribe to scanning progress for current pre-alert if available
    if (this.currentSession && this.currentSession.preAlertId) {
      this.subscribeToCurrentPreAlertProgress();
    }

    // Subscribe to order status updates for current order if available
    if (this.orderId || this.sessionConfig.orderId) {
      this.subscribeToCurrentOrderUpdates();
    }
  }

  /**
   * Subscribe to scanning progress for current pre-alert
   */
  private subscribeToCurrentPreAlertProgress(): void {
    if (!this.currentSession?.preAlertId) return;

    this.signalRService.subscribeToScanningProgress(this.currentSession.preAlertId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(progressUpdate => {
      this.handleRealtimeScanningProgress(progressUpdate);
    });
  }

  /**
   * Subscribe to order status updates for current order
   */
  private subscribeToCurrentOrderUpdates(): void {
    const orderId = this.orderId || this.sessionConfig.orderId;
    if (!orderId) return;

    this.signalRService.subscribeToOrderStatusUpdates(orderId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(statusUpdate => {
      this.handleRealtimeOrderStatusUpdate(statusUpdate);
    });
  }

  /**
   * Handle real-time scan activity from other users
   */
  private handleRealtimeScanActivity(scanActivity: ScanActivityBroadcast): void {
    // Don't show our own scans in real-time activity
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && scanActivity.scannerUserId === currentUser.id) {
      return;
    }

    // Add to recent activity
    this.realtimeScanActivity.unshift(scanActivity);
    if (this.realtimeScanActivity.length > 20) {
      this.realtimeScanActivity = this.realtimeScanActivity.slice(0, 20);
    }

    // === CARTON LIST REAL-TIME UPDATES ===
    // Update carton list if the scan affects the current pre-alert
    if (this.currentCartonList && scanActivity.preAlertId === this.currentCartonList.preAlertId) {
      this.updateCartonListFromScanActivity(scanActivity);
    }

    // Update active scanners tracking
    this.activeScanners.set(scanActivity.scannerUserId, {
      userName: scanActivity.scannerUserName,
      lastActivity: scanActivity.scanTime,
      scanCount: (this.activeScanners.get(scanActivity.scannerUserId)?.scanCount || 0) + 1
    });

    // Show notification for same pre-alert scanning
    if (this.showRealtimeNotifications &&
        this.currentSession &&
        scanActivity.preAlertId === this.currentSession.preAlertId) {
      this.showRealtimeNotification(
        'scan',
        `${scanActivity.scannerUserName} scanned ${scanActivity.barcode}`,
        scanActivity.success ? 'success' : 'warning'
      );
    }

    // Broadcast our own scan activity if this scan was successful
    if (scanActivity.success && this.sessionActive) {
      this.broadcastOwnScanActivity(scanActivity);
    }
  }

  /**
   * Handle real-time scanning progress updates
   */
  private handleRealtimeScanningProgress(progressUpdate: ScanningProgressUpdate): void {
    // Update progress tracking
    this.realtimeProgressUpdates.unshift(progressUpdate);
    if (this.realtimeProgressUpdates.length > 10) {
      this.realtimeProgressUpdates = this.realtimeProgressUpdates.slice(0, 10);
    }

    // Update UI if this is for our current pre-alert
    if (this.currentSession && progressUpdate.preAlertId === this.currentSession.preAlertId) {
      // Update our local progress with real-time data
      this.progress.completionPercentage = progressUpdate.completionPercentage;
      this.progress.scannedCount = progressUpdate.scannedCartons;

      // Show progress notification
      if (this.showRealtimeNotifications) {
        this.showRealtimeNotification(
          'progress',
          `Pre-alert progress: ${progressUpdate.completionPercentage}% (${progressUpdate.scannedCartons}/${progressUpdate.totalCartons})`,
          'info'
        );
      }

      // Update collaborative scanning info
      if (progressUpdate.activeScanner && progressUpdate.activeScanner !== this.authService.getCurrentUser()?.username) {
        this.showRealtimeNotification(
          'collaborative',
          `${progressUpdate.activeScanner} is also scanning this pre-alert`,
          'info'
        );
      }
    }
  }

  /**
   * Handle real-time order status updates
   */
  private handleRealtimeOrderStatusUpdate(statusUpdate: OrderStatusUpdate): void {
    // Add to recent status updates
    this.realtimeOrderStatusUpdates.unshift(statusUpdate);
    if (this.realtimeOrderStatusUpdates.length > 10) {
      this.realtimeOrderStatusUpdates = this.realtimeOrderStatusUpdates.slice(0, 10);
    }

    // Update UI if this is for our current order
    const currentOrderId = this.orderId || this.sessionConfig.orderId;
    if (currentOrderId && statusUpdate.orderId === currentOrderId) {
      // Refresh our order status
      this.loadOrderScanningStatus(statusUpdate.orderId);

      // Show status change notification
      if (this.showRealtimeNotifications) {
        this.showRealtimeNotification(
          'status',
          `Order status updated: ${statusUpdate.previousStatus} → ${statusUpdate.newStatus}`,
          'success'
        );

        // Play celebration sound for significant advancement
        if (this.isWorkflowAdvancement(statusUpdate.previousStatus, statusUpdate.newStatus)) {
          this.playSuccessSound(true);
          this.vibrate([100, 50, 100, 50, 100]);
        }
      }
    }
  }

  /**
   * Broadcast our own scan activity to other connected users
   */
  private async broadcastOwnScanActivity(scanActivity: ScanActivityBroadcast): Promise<void> {
    try {
      await this.signalRService.broadcastScanActivity(scanActivity);
    } catch (error) {
      console.warn('Failed to broadcast scan activity:', error);
    }
  }

  /**
   * Join collaborative scanning session for current pre-alert
   */
  private async joinCollaborativeScanning(): Promise<void> {
    if (!this.currentSession?.preAlertId || !this.sessionActive) return;

    try {
      const currentUser = this.authService.getCurrentUser();
      if (!currentUser) return;

      await this.signalRService.joinScanningSession(
        this.currentSession.preAlertId,
        currentUser.id,
        currentUser.username
      );

      // Subscribe to collaborative updates
      this.signalRService.subscribeToCollaborativeScanning(this.currentSession.sessionId).pipe(
        takeUntil(this.destroy$)
      ).subscribe(collaborativeUpdate => {
        this.collaborativeSession = collaborativeUpdate;
        this.isCollaborativeMode = collaborativeUpdate.activeUsers.length > 1;
      });

    } catch (error) {
      console.warn('Failed to join collaborative scanning:', error);
    }
  }

  /**
   * Leave collaborative scanning session
   */
  private async leaveCollaborativeScanning(): Promise<void> {
    if (!this.currentSession?.preAlertId) return;

    try {
      const currentUser = this.authService.getCurrentUser();
      if (!currentUser) return;

      await this.signalRService.leaveScanningSession(
        this.currentSession.preAlertId,
        currentUser.id
      );

      this.collaborativeSession = null;
      this.isCollaborativeMode = false;

    } catch (error) {
      console.warn('Failed to leave collaborative scanning:', error);
    }
  }

  /**
   * Show real-time notification
   */
  private showRealtimeNotification(type: string, message: string, level: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.showRealtimeNotifications) return;

    this.notificationQueue.unshift({
      type,
      message,
      timestamp: new Date()
    });

    // Limit notification queue
    if (this.notificationQueue.length > 5) {
      this.notificationQueue = this.notificationQueue.slice(0, 5);
    }

    // Show toast notification
    this.showAlertMessage(`🔴 ${message}`, level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'success');
  }

  /**
   * Cleanup SignalR subscriptions
   */
  private async cleanupSignalRSubscriptions(): Promise<void> {
    try {
      // Leave collaborative scanning if active
      if (this.isCollaborativeMode) {
        await this.leaveCollaborativeScanning();
      }

      // Unsubscribe from scanning events
      await this.signalRService.unsubscribeFromScanningEvents(
        this.currentSession?.preAlertId,
        this.orderId || this.sessionConfig.orderId
      );

    } catch (error) {
      console.warn('Error cleaning up SignalR subscriptions:', error);
    }
  }

  // === SIGNALR UI GETTERS ===

  get activeScannersCount(): number {
    return this.activeScanners.size;
  }

  get activeScannersArray(): { userId: string; userName: string; lastActivity: Date; scanCount: number }[] {
    return Array.from(this.activeScanners.entries()).map(([userId, data]) => ({
      userId,
      ...data
    }));
  }

  get recentRealtimeActivity(): ScanActivityBroadcast[] {
    return this.realtimeScanActivity.slice(0, 5);
  }

  get hasRealtimeUpdates(): boolean {
    return this.realtimeScanActivity.length > 0 ||
           this.realtimeProgressUpdates.length > 0 ||
           this.realtimeOrderStatusUpdates.length > 0;
  }

  get collaborativeInfo(): string {
    if (!this.isCollaborativeMode || !this.collaborativeSession) {
      return 'Solo scanning';
    }

    const activeCount = this.collaborativeSession.activeUsers.length;
    const otherUsers = this.collaborativeSession.activeUsers
      .filter(user => user.userId !== this.authService.getCurrentUser()?.id)
      .map(user => user.userName)
      .join(', ');

    return `Collaborative scanning with ${activeCount - 1} others: ${otherUsers}`;
  }

  // === SIGNALR UI ACTIONS ===

  toggleRealtimeNotifications(): void {
    this.showRealtimeNotifications = !this.showRealtimeNotifications;

    if (this.showRealtimeNotifications) {
      this.showAlertMessage('Real-time notifications enabled', 'success');
    } else {
      this.showAlertMessage('Real-time notifications disabled', 'warning');
    }
  }

  clearRealtimeActivity(): void {
    this.realtimeScanActivity = [];
    this.realtimeProgressUpdates = [];
    this.realtimeOrderStatusUpdates = [];
    this.notificationQueue = [];
    this.activeScanners.clear();

    this.showAlertMessage('Real-time activity cleared', 'success');
  }

  async startCollaborativeScanning(): Promise<void> {
    if (this.isCollaborativeMode) {
      this.showAlertMessage('Already in collaborative mode', 'warning');
      return;
    }

    await this.joinCollaborativeScanning();
    this.showAlertMessage('Joined collaborative scanning session', 'success');
  }

  async stopCollaborativeScanning(): Promise<void> {
    if (!this.isCollaborativeMode) {
      this.showAlertMessage('Not in collaborative mode', 'warning');
      return;
    }

    await this.leaveCollaborativeScanning();
    this.showAlertMessage('Left collaborative scanning session', 'success');
  }

  forceSignalRReconnect(): void {
    this.signalRService.forceReconnect();
    this.showAlertMessage('Reconnecting to real-time service...', 'warning');
  }

  // === CARTON LIST MANAGEMENT METHODS ===

  /**
   * Initialize carton list functionality
   */
  private initializeCartonList(): void {
    // Load carton list when a scan session is active and has a pre-alert
    this.currentSession$.pipe(
      takeUntil(this.destroy$),
      switchMap(session => {
        if (session && session.preAlertId) {
          return this.loadCartonListForPreAlert(session.preAlertId);
        }
        return of(null);
      })
    ).subscribe();

    // Set up auto-refresh if enabled
    if (this.cartonListAutoRefresh) {
      this.setupCartonListAutoRefresh();
    }
  }

  /**
   * Load carton list for a specific pre-alert
   * @param ladingBill The lading bill number (e.g., "869-00303446")
   */
  private loadCartonListForPreAlert(ladingBill: string): Observable<PreAlertCartonList | null> {
    this.cartonListLoading = true;
    this.cartonListError = null;

    return this.mobileScanningService.getPreAlertCartonList(ladingBill).pipe(
      tap(cartonList => {
        this.currentCartonList = cartonList;
        this.cartonListLoading = false;
        this.showCartonListPanel = true; // Auto-show panel when data loads
        this.applyCurrentFilter();
      }),
      catchError(error => {
        this.cartonListError = error.message || 'Failed to load carton list';
        this.cartonListLoading = false;
        console.error('Error loading carton list:', error);
        return of(null);
      })
    );
  }

  /**
   * Setup auto-refresh for carton list
   */
  private setupCartonListAutoRefresh(): void {
    timer(this.cartonListRefreshInterval, this.cartonListRefreshInterval).pipe(
      takeUntil(this.destroy$),
      filter(() => this.cartonListAutoRefresh && !!this.currentCartonList),
      switchMap(() => {
        if (this.currentCartonList) {
          return this.loadCartonListForPreAlert(this.currentCartonList.ladingBill);
        }
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Apply current filter to carton list
   */
  private applyCurrentFilter(): void {
    if (!this.currentCartonList) {
      this.searchResults = [];
      return;
    }

    const filter: CartonListFilter = {
      ...this.cartonListFilter,
      searchTerm: this.cartonSearchTerm || undefined
    };

    // Apply tab-based filtering
    if (this.activeCartonListTab !== 'all') {
      filter.status = this.activeCartonListTab === 'scanned' ? 'scanned' : 'unscanned';
    }

    this.mobileScanningService.getFilteredCartonList(this.currentCartonList.ladingBill, filter).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: filteredCartons => {
        this.searchResults = filteredCartons;
      },
      error: (error) => {
        console.error('[Mobile Scanner] Filter error:', error);
        this.searchResults = [];
        this.showAlertMessage(error.message || 'Failed to load carton list', 'error');

        // Log to backend for remote debugging
        this.logErrorToBackend('applyCurrentFilter', error, {
          tab: this.activeCartonListTab,
          filter: filter,
          preAlert: this.currentCartonList?.ladingBill
        });
      }
    });
  }

  /**
   * Refresh carton list manually
   */
  refreshCartonList(): void {
    if (this.currentCartonList) {
      this.loadCartonListForPreAlert(this.currentCartonList.ladingBill).subscribe();
    }
  }

  /**
   * Toggle carton list panel visibility
   */
  toggleCartonListPanel(): void {
    this.showCartonListPanel = !this.showCartonListPanel;
  }

  /**
   * Toggle carton list panel collapsed state
   */
  toggleCartonListCollapsed(): void {
    this.cartonListPanelCollapsed = !this.cartonListPanelCollapsed;
  }

  /**
   * Switch active tab in carton list
   */
  setActiveCartonListTab(tab: 'all' | 'scanned' | 'unscanned'): void {
    this.activeCartonListTab = tab;
    this.applyCurrentFilter();
  }

  /**
   * Handle search input changes
   */
  onCartonSearchChange(): void {
    // Debounce search to avoid too many API calls
    setTimeout(() => {
      this.applyCurrentFilter();
    }, 300);
  }

  /**
   * Clear search
   */
  clearCartonSearch(): void {
    this.cartonSearchTerm = '';
    this.applyCurrentFilter();
  }

  /**
   * Update filter and refresh results
   */
  updateCartonListFilter(filter: Partial<CartonListFilter>): void {
    this.cartonListFilter = { ...this.cartonListFilter, ...filter };
    this.applyCurrentFilter();
  }

  // === CARTON LIST UI GETTERS ===

  get hasCartonList(): boolean {
    return !!this.currentCartonList;
  }

  get totalCartonsCount(): number {
    return this.currentCartonList?.totalCartons || 0;
  }

  get scannedCartonsCount(): number {
    return this.currentCartonList?.scannedCount || 0;
  }

  get unscannedCartonsCount(): number {
    return this.currentCartonList?.unscannedCount || 0;
  }

  get cartonListProgress(): number {
    return this.currentCartonList?.completionPercentage || 0;
  }

  get filteredCartonsCount(): number {
    return this.searchResults.length;
  }

  get allCartons(): CartonListItem[] {
    return this.currentCartonList?.cartons || [];
  }

  get scannedCartonsFromList(): CartonListItem[] {
    return this.allCartons.filter(c => c.isScanned);
  }

  get unscannedCartons(): CartonListItem[] {
    return this.allCartons.filter(c => !c.isScanned);
  }

  get cartonListTitle(): string {
    if (!this.currentCartonList) return 'Carton List';
    return `Pre-Alert: ${this.currentCartonList.ladingBill}`;
  }

  get cartonListProgressClass(): string {
    const progress = this.cartonListProgress;
    if (progress >= 100) return 'bg-success';
    if (progress >= 75) return 'bg-info';
    if (progress >= 50) return 'bg-warning';
    return 'bg-danger';
  }

  // === CARTON LIST UI ACTIONS ===

  highlightCartonInList(cartonNumber: string): void {
    // This method can be called when a carton is scanned to highlight it in the list
    // Implementation would scroll to and highlight the specific carton
    console.log('Highlighting carton in list:', cartonNumber);
  }

  onCartonItemClick(carton: CartonListItem): void {
    // Handle clicking on a carton in the list
    if (!carton.isScanned) {
      // Maybe focus on the scanner to scan this carton
      this.showAlertMessage(`Focus on scanning: ${carton.cartonNumber}`, 'success');
    }
  }

  /**
   * Update carton list from real-time scan activity
   */
  private updateCartonListFromScanActivity(scanActivity: ScanActivityBroadcast): void {
    if (!this.currentCartonList) return;

    // Find the carton in the current list
    const cartonIndex = this.currentCartonList.cartons.findIndex(
      c => c.cartonNumber === scanActivity.barcode
    );

    if (cartonIndex >= 0) {
      // Update the carton as scanned
      this.currentCartonList.cartons[cartonIndex] = {
        ...this.currentCartonList.cartons[cartonIndex],
        isScanned: true,
        scannedAt: new Date(scanActivity.scanTime),
        scannedBy: scanActivity.scannerUserName,
        status: 'scanned'
      };

      // Update counts
      this.currentCartonList.scannedCount = this.currentCartonList.cartons.filter(c => c.isScanned).length;
      this.currentCartonList.unscannedCount = this.currentCartonList.totalCartons - this.currentCartonList.scannedCount;
      this.currentCartonList.completionPercentage = Math.round(
        (this.currentCartonList.scannedCount / this.currentCartonList.totalCartons) * 100
      );
      this.currentCartonList.lastUpdated = new Date();

      // Refresh the filtered results
      this.applyCurrentFilter();

      // Show notification
      this.showAlertMessage(
        `${scanActivity.barcode} scanned by ${scanActivity.scannerUserName}`,
        'success'
      );

      // Highlight the carton in the list
      this.highlightCartonInList(scanActivity.barcode);
    }
  }

  /**
   * Update carton list from own scan activity
   */
  private updateCartonListFromOwnScan(cartonNo: string, scanResult: ScanWithStatusResult): void {
    if (!this.currentCartonList) return;

    // Find the carton in the current list
    const cartonIndex = this.currentCartonList.cartons.findIndex(
      c => c.cartonNumber === cartonNo
    );

    if (cartonIndex >= 0) {
      // Update the carton as scanned
      this.currentCartonList.cartons[cartonIndex] = {
        ...this.currentCartonList.cartons[cartonIndex],
        isScanned: scanResult.success,
        scannedAt: new Date(),
        scannedBy: this.authService.getCurrentUser()?.username || 'current_user',
        status: scanResult.success ? 'scanned' : 'pending',
        errorMessage: scanResult.success ? undefined : scanResult.message
      };

      // Update counts and refresh
      this.updateCartonListStats();
      this.applyCurrentFilter();
    }
  }

  /**
   * Update carton list statistics
   */
  private updateCartonListStats(): void {
    if (!this.currentCartonList) return;

    this.currentCartonList.scannedCount = this.currentCartonList.cartons.filter(c => c.isScanned).length;
    this.currentCartonList.unscannedCount = this.currentCartonList.totalCartons - this.currentCartonList.scannedCount;
    this.currentCartonList.completionPercentage = Math.round(
      (this.currentCartonList.scannedCount / this.currentCartonList.totalCartons) * 100
    );
    this.currentCartonList.lastUpdated = new Date();
  }

  // === AUTO-DETECTION METHODS ===

  /**
   * Detect and load pre-alert from a scanned carton
   */
  private async detectAndLoadPreAlert(cartonNo: string): Promise<void> {
    this.isDetectingPreAlert = true;

    try {
      // This will call the mobile scanning service method we'll create
      const detection = await this.mobileScanningService.findPreAlertByCarton(cartonNo).toPromise();

      if (detection && detection.found && detection.preAlert) {
        this.autoDetectedPreAlert = detection.preAlert;
        this.preAlertLocked = true;

        // Load carton list for this pre-alert
        await this.loadCartonListForDetectedPreAlert(detection.preAlert.id, detection.cartons);

        // Show success message
        this.showAlertMessage(
          `Pre-Alert ${detection.preAlert.ladingBill} detected (${detection.preAlert.totalCartons} cartons)`,
          'success'
        );

        // Auto-show carton list panel after detection
        this.showCartonListPanel = true;
        this.cartonListPanelCollapsed = false;
        this.showPreAlertInfo = true;

        this.playSuccessSound(true);
      } else {
        this.showAlertMessage(
          `Carton ${cartonNo} not found in any pre-alert`,
          'warning'
        );
        this.playErrorSound();
      }
    } catch (error) {
      console.error('Error detecting pre-alert:', error);
      this.showAlertMessage('Error detecting pre-alert', 'error');
      this.playErrorSound();
    } finally {
      this.isDetectingPreAlert = false;
    }
  }

  /**
   * Load carton list for detected pre-alert
   */
  private async loadCartonListForDetectedPreAlert(preAlertId: string, cartons: any[]): Promise<void> {
    this.cartonListLoading = true;
    this.cartonListError = null;

    try {
      // Create carton list from detection result
      this.currentCartonList = {
        preAlertId: preAlertId,
        ladingBill: this.autoDetectedPreAlert.ladingBill,
        totalCartons: cartons.length,
        scannedCount: 0,
        unscannedCount: cartons.length,
        completionPercentage: 0,
        cartons: cartons.map(carton => ({
          cartonNumber: carton.cartonNumber,
          carrierTrackingNumber: carton.carrierTrackingNumber,
          orderId: carton.orderId,
          isScanned: carton.isScanned,
          scannedAt: carton.isScanned ? new Date() : undefined,
          status: carton.isScanned ? 'scanned' as const : 'pending' as const
        })),
        lastUpdated: new Date()
      };

      this.applyCurrentFilter();
    } catch (error) {
      console.error('Error loading carton list for detected pre-alert:', error);
      this.cartonListError = 'Failed to load carton list';
    } finally {
      this.cartonListLoading = false;
    }
  }

  /**
   * Validate if a carton belongs to the currently detected pre-alert
   */
  private validateCartonAgainstPreAlert(cartonNo: string): boolean {
    if (!this.currentCartonList) return true;

    return this.currentCartonList.cartons.some(
      carton => carton.cartonNumber === cartonNo
    );
  }

  /**
   * Check if carton is in the detected pre-alert (for legacy compatibility)
   */
  private isCartonInDetectedPreAlert(cartonNo: string): boolean {
    if (!this.currentCartonList) return false;

    return this.currentCartonList.cartons.some(
      carton => carton.cartonNumber === cartonNo
    );
  }

  /**
   * Update carton list status after scanning
   */
  private updateCartonListStatus(cartonNo: string): void {
    if (!this.currentCartonList) return;

    const carton = this.currentCartonList.cartons.find(c => c.cartonNumber === cartonNo);
    if (carton && !carton.isScanned) {
      carton.isScanned = true;
      carton.scannedAt = new Date();
      carton.status = 'scanned';

      // Update counts
      this.currentCartonList.scannedCount++;
      this.currentCartonList.unscannedCount--;
      this.currentCartonList.completionPercentage = Math.round(
        (this.currentCartonList.scannedCount / this.currentCartonList.totalCartons) * 100
      );
      this.currentCartonList.lastUpdated = new Date();

      // Re-apply current filter to update display
      this.applyCurrentFilter();
    }
  }

  /**
   * Reset pre-alert detection (allow user to start over)
   */
  resetPreAlert(): void {
    if (!this.autoDetectedPreAlert) return;

    this.triggerHapticFeedback('button');
    const confirmReset = confirm(
      `Reset pre-alert detection? This will clear current scanning session for ${this.autoDetectedPreAlert.ladingBill}.`
    );

    if (confirmReset) {
      this.autoDetectedPreAlert = null;
      this.preAlertLocked = false;
      this.currentCartonList = null;
      this.scannedCartons = [];
      this.showCartonListPanel = false;
      this.showPreAlertInfo = true;

      this.showAlertMessage('Pre-alert reset. Scan a carton to detect new pre-alert.', 'success');
      this.playSuccessSound(true);
    }
  }

  // NOTE: cartonListTitle getter is already defined at line 2200-2203, removing duplicate

  // NOTE: hasCartonList getter is already defined at line 2164-2166, removing duplicate

  // === SCAN PROGRESS SIGNALR METHODS (NEW) ===

  /**
   * Initialize the dedicated Scan Progress SignalR connection
   */
  private async initializeScanProgressSignalR(): Promise<void> {
    try {
      const authToken = this.authService.getToken() || '';
      if (!authToken) {
        console.warn('No auth token available for Scan Progress SignalR');
        return;
      }

      await this.scanProgressSignalR.startConnection(authToken);
      console.log('Scan Progress SignalR initialized successfully');

      // Subscribe to scan progress updates
      this.scanProgressSubscription = this.scanProgressSignalR.scanProgress$
        .pipe(takeUntil(this.destroy$))
        .subscribe(update => {
          if (update) {
            this.handleScanProgressSignalRUpdate(update);
          }
        });

      // Monitor connection state
      this.scanProgressSignalR.connectionState$
        .pipe(takeUntil(this.destroy$))
        .subscribe(state => {
          this.isSignalRProgressConnected = state === signalR.HubConnectionState.Connected; // Connected
          console.log('Scan Progress SignalR state:', state);
        });

      // === PHASE 5: LISTEN FOR DUPLICATE SCAN EVENTS ===
      this.scanProgressSignalR.onDuplicateScan((eventData: any) => {
        console.log('Duplicate scan event received from SignalR:', eventData);

        // Show toast notification for other users' duplicate attempts
        // Only show if it's not our own scan (to avoid duplicate notifications)
        if (eventData.cartonNumber && !this.showDuplicateWarning) {
          this.showAlertMessage(
            `Duplicate scan attempt on ${eventData.cartonNumber} detected`,
            'warning'
          );
        }
      });

    } catch (error) {
      console.error('Failed to initialize Scan Progress SignalR:', error);
      // Show user-friendly error message
      this.showAlertMessage('Real-time updates unavailable', 'warning');
    }
  }

  /**
   * Handle real-time scan progress updates from SignalR
   */
  private handleScanProgressSignalRUpdate(update: ScanProgressUpdate): void {
    console.log('Processing scan progress update:', update);

    this.scanProgressRT = update;
    this.showModernProgressCard = true;

    // Trigger scan animation
    this.progressScanAnimation = true;
    setTimeout(() => this.progressScanAnimation = false, 600);

    // Add to recent scans (keep last 5)
    this.recentProgressScans.unshift({
      cartonNumber: update.cartonNumber,
      timestamp: new Date(update.scannedAt),
      scannedBy: update.scannedBy
    });
    if (this.recentProgressScans.length > 5) {
      this.recentProgressScans.pop();
    }

    // Show notification
    this.showAlertMessage(
      `✓ ${update.cartonNumber} scanned - ${update.scannedCartons}/${update.totalCartons} (${update.percentage}%)`,
      'success'
    );

    // Play success sound and vibrate
    this.playSuccessSound(true);
    this.vibrate([100]);

    // Check if complete
    if (update.percentage === 100) {
      this.onScanProgressComplete(update);
    }
  }

  /**
   * Handle scan progress completion
   */
  private onScanProgressComplete(update: ScanProgressUpdate): void {
    console.log('All cartons scanned for pre-alert:', update.ladingBill);

    this.showAlertMessage(
      `🎉 All ${update.totalCartons} cartons scanned for ${update.ladingBill}!`,
      'success'
    );

    // === PHASE 4: Trigger celebration ===
    if (!this.hasTriggeredCelebration) {
      this.triggerCompletionCelebration();
      this.hasTriggeredCelebration = true;
    }
  }

  /**
   * Join SignalR room for a specific pre-alert (when detected)
   */
  async joinPreAlertProgressRoom(ladingBill: string): Promise<void> {
    try {
      if (this.currentPreAlertLadingBill && this.currentPreAlertLadingBill !== ladingBill) {
        await this.scanProgressSignalR.leavePreAlertRoom(this.currentPreAlertLadingBill);
      }

      this.currentPreAlertLadingBill = ladingBill;
      await this.scanProgressSignalR.joinPreAlertRoom(ladingBill);
      console.log(`Joined scan progress room for: ${ladingBill}`);

      // Fetch initial progress from API
      await this.fetchInitialScanProgress(ladingBill);

      this.showModernProgressCard = true;

    } catch (error) {
      console.error('Failed to join scan progress room:', error);
    }
  }

  /**
   * Fetch initial scan progress from API endpoint
   */
  private async fetchInitialScanProgress(ladingBill: string): Promise<void> {
    try {
      // Use HttpClient to fetch progress from API
      // Endpoint: GET /api/pre-alerts/scan-progress/{ladingBill}
      const response: any = await this.http.get(
        `${environment.apiUrl}/pre-alerts/scan-progress/${ladingBill}`
      ).toPromise();

      if (response) {
        this.scanProgressRT = {
          ladingBill: response.ladingBill,
          scannedCartons: response.scannedCartons,
          totalCartons: response.totalCartons,
          percentage: response.percentage,
          cartonNumber: '',
          scannedAt: new Date(),
          scannedBy: ''
        };
        this.showModernProgressCard = true;
        console.log('Initial scan progress fetched:', this.scanProgressRT);
      }
    } catch (error) {
      console.error('Failed to fetch initial scan progress:', error);
    }
  }

  /**
   * Cleanup scan progress SignalR connection
   */
  private async cleanupScanProgressSignalR(): Promise<void> {
    try {
      // Unsubscribe from progress updates
      if (this.scanProgressSubscription) {
        this.scanProgressSubscription.unsubscribe();
      }

      // Leave current pre-alert room
      if (this.currentPreAlertLadingBill) {
        await this.scanProgressSignalR.leavePreAlertRoom(this.currentPreAlertLadingBill);
      }

      // Stop SignalR connection
      await this.scanProgressSignalR.stopConnection();
      console.log('Scan Progress SignalR cleanup complete');

    } catch (error) {
      console.warn('Error cleaning up Scan Progress SignalR:', error);
    }
  }

  // === UPDATED AUTO-DETECTION METHOD ===

  /**
   * Enhanced detect and load pre-alert with SignalR integration
   */
  private async detectAndLoadPreAlertEnhanced(cartonNo: string): Promise<void> {
    this.isDetectingPreAlert = true;

    try {
      const detection = await this.mobileScanningService.findPreAlertByCarton(cartonNo).toPromise();

      if (detection && detection.found && detection.preAlert) {
        this.autoDetectedPreAlert = detection.preAlert;
        this.preAlertLocked = true;

        // Load carton list for this pre-alert
        await this.loadCartonListForDetectedPreAlert(detection.preAlert.id, detection.cartons);

        // JOIN SCAN PROGRESS SIGNALR ROOM
        await this.joinPreAlertProgressRoom(detection.preAlert.ladingBill);

        // Show success message
        this.showAlertMessage(
          `Pre-Alert ${detection.preAlert.ladingBill} detected (${detection.preAlert.totalCartons} cartons)`,
          'success'
        );

        // Auto-show carton list and progress panels
        this.showCartonListPanel = true;
        this.showModernProgressCard = true;
        this.cartonListPanelCollapsed = false;
        this.showPreAlertInfo = true;

        this.playSuccessSound(true);
      } else {
        this.showAlertMessage(
          `Carton ${cartonNo} not found in any pre-alert`,
          'warning'
        );
        this.playErrorSound();
      }
    } catch (error) {
      console.error('Error detecting pre-alert:', error);
      this.showAlertMessage('Error detecting pre-alert', 'error');
      this.playErrorSound();
    } finally {
      this.isDetectingPreAlert = false;
    }
  }

  // === UI GETTERS FOR MODERN PROGRESS DISPLAY ===

  get modernProgressPercentage(): number {
    return this.scanProgressRT?.percentage || 0;
  }

  get modernProgressScannedCount(): number {
    return this.scanProgressRT?.scannedCartons || 0;
  }

  get modernProgressTotalCount(): number {
    return this.scanProgressRT?.totalCartons || 0;
  }

  get modernProgressRemainingCount(): number {
    return this.modernProgressTotalCount - this.modernProgressScannedCount;
  }

  get modernProgressIsComplete(): boolean {
    return this.modernProgressPercentage >= 100;
  }

  get modernProgressBarClass(): string {
    const pct = this.modernProgressPercentage;
    if (pct >= 100) return 'bg-success';
    if (pct >= 75) return 'bg-info';
    if (pct >= 50) return 'bg-warning';
    return 'bg-primary';
  }

  get recentProgressScansDisplay(): Array<{cartonNumber: string, timestamp: Date, scannedBy: string}> {
    return this.recentProgressScans.slice(0, 5);
  }

  // === PHASE 4: ENHANCED UX CELEBRATION METHODS ===

  /**
   * Initialize Web Audio API context
   */
  private initializeAudioContext(): void {
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      try {
        this.audioContext = new AudioContext();
        console.log('Web Audio API initialized');
      } catch (error) {
        console.warn('Failed to initialize Web Audio API:', error);
      }
    }
  }

  /**
   * Load user preferences from localStorage
   */
  private loadUserPreferences(): void {
    try {
      const soundPref = localStorage.getItem('scannerSoundEnabled');
      this.soundEnabled = soundPref !== 'false';

      const hapticPref = localStorage.getItem('scannerHapticEnabled');
      this.hapticEnabled = hapticPref !== 'false';

      console.log('User preferences loaded:', {
        sound: this.soundEnabled,
        haptic: this.hapticEnabled
      });
    } catch (error) {
      console.warn('Failed to load user preferences:', error);
    }
  }

  /**
   * Trigger celebration animation and effects on 100% completion
   */
  private triggerCompletionCelebration(): void {
    console.log('🎉 All cartons scanned! Triggering celebration...');

    // Confetti burst from bottom
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#28a745', '#20c997', '#17a2b8', '#ffc107']
    });

    // Second burst from left after delay
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#28a745', '#20c997']
      });
    }, 250);

    // Third burst from right
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#28a745', '#20c997']
      });
    }, 400);

    // Play success sound
    this.playSuccessSoundV2();

    // Vibrate device (mobile)
    this.triggerHapticFeedback('success');

    // Show completion modal
    this.showCompletionModal = true;
  }

  /**
   * Play success sound using Web Audio API
   */
  private playSuccessSoundV2(): void {
    if (!this.soundEnabled || !this.audioContext) {
      return;
    }

    try {
      // Create success chord: C major (C-E-G)
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(523.25, this.audioContext.currentTime); // C5
      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn('Failed to play success sound:', error);
    }
  }

  /**
   * Play scan beep sound
   */
  private playScanBeep(): void {
    if (!this.soundEnabled || !this.audioContext) {
      return;
    }

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.2, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

      oscillator.start();
      oscillator.stop(this.audioContext.currentTime + 0.1);
    } catch (error) {
      console.warn('Failed to play scan beep:', error);
    }
  }

  /**
   * Toggle sound effects on/off
   */
  toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    localStorage.setItem('scannerSoundEnabled', String(this.soundEnabled));
    console.log('Sound effects:', this.soundEnabled ? 'enabled' : 'disabled');

    // Play test sound when enabling
    if (this.soundEnabled) {
      this.playScanBeep();
    }
  }

  /**
   * Toggle haptic feedback on/off
   */
  toggleHaptic(): void {
    this.hapticEnabled = !this.hapticEnabled;
    localStorage.setItem('scannerHapticEnabled', String(this.hapticEnabled));
    console.log('Haptic feedback:', this.hapticEnabled ? 'enabled' : 'disabled');

    // Test vibration when enabling
    if (this.hapticEnabled) {
      this.triggerHapticFeedback('scanStart');
    }
  }

  /**
   * View scan summary (called from completion modal)
   */
  viewScanSummary(): void {
    this.showCompletionModal = false;
    console.log('View scan summary:', this.scanProgressRT);

    // Show the progress card with full details
    this.showModernProgressCard = true;

    // Optionally scroll to progress card
    setTimeout(() => {
      const progressCard = document.querySelector('.modern-progress-card');
      if (progressCard) {
        progressCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  /**
   * Start a new scan session (called from completion modal)
   */
  startNewScan(): void {
    this.showCompletionModal = false;
    this.hasTriggeredCelebration = false;
    this.showModernProgressCard = false;
    this.recentProgressScans = [];
    this.scanProgressRT = null;

    // Reset carton list
    this.clearAll();

    console.log('Starting new scan session');
    this.showAlertMessage('Ready to scan new pre-alert', 'success');
  }

  /**
   * Toggle settings panel visibility
   */
  toggleSettings(): void {
    this.showSettings = !this.showSettings;
  }

  // === PHASE 5: DUPLICATE SCAN WARNING METHODS ===

  /**
   * Handle duplicate scan detection
   */
  private handleDuplicateScan(duplicateInfo: any): void {
    console.log('Duplicate scan detected:', duplicateInfo);

    // Process duplicate info from backend
    this.duplicateScanInfo = {
      cartonNumber: duplicateInfo.cartonNumber,
      ladingBill: duplicateInfo.ladingBill || '',
      isDuplicate: true,
      previousScan: {
        scannedBy: duplicateInfo.previousScan?.scannedBy || 'Unknown',
        scannedAt: new Date(duplicateInfo.previousScan?.scannedAt || Date.now()),
        scannedAtFormatted: duplicateInfo.previousScan?.scannedAtFormatted || '',
        minutesAgo: duplicateInfo.previousScan?.minutesAgo || 0,
        isSameUser: duplicateInfo.previousScan?.isSameUser || false
      },
      message: duplicateInfo.message || 'This carton has already been scanned'
    };

    this.showDuplicateWarning = true;

    // Play error sound
    this.playErrorSound();

    // Haptic feedback (long vibration)
    this.vibrate([400, 100, 400]);

    // Show toast notification
    this.showAlertMessage(
      `This carton was already scanned ${this.duplicateScanInfo.previousScan.minutesAgo} minutes ago by ${this.duplicateScanInfo.previousScan.scannedBy}`,
      'warning'
    );
  }

  /**
   * View complete scan history for a carton
   */
  async viewScanHistory(cartonNumber: string): Promise<void> {
    this.loadingScanHistory = true;

    try {
      const response: any = await this.http.get<any>(
        `${environment.apiUrl}/api/mobile-scanning/scan-history/${cartonNumber}`
      ).toPromise();

      this.scanHistory = response.data || response;
      console.log('Scan history loaded:', this.scanHistory);

    } catch (error) {
      console.error('Failed to load scan history:', error);
      this.showAlertMessage('Failed to load scan history', 'error');
    } finally {
      this.loadingScanHistory = false;
    }
  }

  /**
   * Close duplicate warning modal
   */
  closeDuplicateWarning(): void {
    this.showDuplicateWarning = false;
    this.duplicateScanInfo = null;
    this.scanHistory = null;
  }

  // === BATCHED STATUS UPDATE METHODS ===

  /**
   * Queue a carton for batched status update
   * Instead of updating database immediately, we collect cartons and update in batches
   * This reduces network requests from 140 individual calls to ~7 batch calls
   */
  private queueStatusUpdate(cartonNo: string): void {
    // Add to pending queue (avoid duplicates)
    if (!this.pendingStatusUpdates.includes(cartonNo)) {
      this.pendingStatusUpdates.push(cartonNo);
      console.log(`[Batch Update] Queued: ${cartonNo} (${this.pendingStatusUpdates.length}/${this.BATCH_SIZE})`);

      // Save queue to localStorage for persistence
      this.saveBatchQueueToStorage();

      // Check if we should flush the batch
      if (this.pendingStatusUpdates.length >= this.BATCH_SIZE) {
        console.log(`[Batch Update] Batch size reached (${this.BATCH_SIZE}) - flushing queue`);
        this.flushBatchUpdate();
      } else {
        // Start/reset the timeout timer
        this.resetBatchTimer();
      }
    }
  }

  /**
   * Reset the batch update timer
   * Ensures batch flushes after 30 seconds even if batch size not reached
   */
  private resetBatchTimer(): void {
    if (this.batchUpdateTimer) {
      clearTimeout(this.batchUpdateTimer);
    }

    this.batchUpdateTimer = setTimeout(() => {
      if (this.pendingStatusUpdates.length > 0) {
        console.log(`[Batch Update] Timeout reached (${this.BATCH_TIMEOUT_MS}ms) - flushing ${this.pendingStatusUpdates.length} cartons`);
        this.flushBatchUpdate();
      }
    }, this.BATCH_TIMEOUT_MS);
  }

  /**
   * Flush all pending status updates to the backend
   * Sends batch update request with all queued cartons
   */
  private async flushBatchUpdate(): Promise<void> {
    if (this.batchUpdateInProgress || this.pendingStatusUpdates.length === 0) {
      return;
    }

    // Clear timer
    if (this.batchUpdateTimer) {
      clearTimeout(this.batchUpdateTimer);
      this.batchUpdateTimer = null;
    }

    this.batchUpdateInProgress = true;
    const cartonsToUpdate = [...this.pendingStatusUpdates]; // Copy array
    const batchSize = cartonsToUpdate.length;

    console.log(`[Batch Update] Flushing ${batchSize} cartons to backend...`, cartonsToUpdate);

    try {
      // Call batch update API endpoint with session context
      const result = await this.mobileScanningService.batchUpdateCartonStatus(
        cartonsToUpdate,
        this.currentSession?.sessionId,
        this.currentCartonList?.ladingBill
      ).toPromise();

      if (result && result.successfulUpdates > 0) {
        console.log(`[Batch Update] ✅ Success! ${result.successfulUpdates}/${batchSize} cartons updated`);

        // Remove successfully updated cartons from queue
        this.pendingStatusUpdates = this.pendingStatusUpdates.filter(
          carton => !cartonsToUpdate.includes(carton)
        );

        this.lastBatchUpdateTime = new Date();

        // Show success feedback (brief, non-intrusive)
        if (result.allSuccessful) {
          this.showAlertMessage(`✅ ${result.successfulUpdates} cartons synced to database`, 'success');
        } else {
          this.showAlertMessage(
            `⚠ ${result.successfulUpdates}/${batchSize} cartons synced (${result.failedUpdates} failed)`,
            'warning'
          );
        }
      }

      // Save updated queue
      this.saveBatchQueueToStorage();

    } catch (error) {
      console.error('[Batch Update] Failed:', error);
      this.showAlertMessage('⚠ Batch sync failed - will retry', 'warning');

      // Keep cartons in queue for retry
      // Next batch will include these cartons
    } finally {
      this.batchUpdateInProgress = false;

      // If there are still pending updates, restart timer
      if (this.pendingStatusUpdates.length > 0) {
        this.resetBatchTimer();
      }
    }
  }

  /**
   * Save batch queue to localStorage for persistence
   * Ensures pending updates survive page refresh or app close
   */
  private saveBatchQueueToStorage(): void {
    try {
      localStorage.setItem('rarr_pending_batch_updates', JSON.stringify({
        cartons: this.pendingStatusUpdates,
        timestamp: new Date().toISOString(),
        sessionId: this.currentSession?.sessionId
      }));
    } catch (error) {
      console.error('[Batch Update] Failed to save queue to storage:', error);
    }
  }

  /**
   * Load batch queue from localStorage on component init
   */
  private loadBatchQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem('rarr_pending_batch_updates');
      if (stored) {
        const data = JSON.parse(stored);
        const age = Date.now() - new Date(data.timestamp).getTime();

        // Only restore if less than 24 hours old
        if (age < 24 * 60 * 60 * 1000 && data.cartons && data.cartons.length > 0) {
          this.pendingStatusUpdates = data.cartons;
          console.log(`[Batch Update] Restored ${this.pendingStatusUpdates.length} pending updates from storage`);

          // Auto-flush restored updates after 5 seconds
          setTimeout(() => {
            if (this.pendingStatusUpdates.length > 0) {
              console.log('[Batch Update] Auto-flushing restored updates');
              this.flushBatchUpdate();
            }
          }, 5000);
        } else {
          localStorage.removeItem('rarr_pending_batch_updates');
        }
      }
    } catch (error) {
      console.error('[Batch Update] Failed to load queue from storage:', error);
      localStorage.removeItem('rarr_pending_batch_updates');
    }
  }

  /**
   * Force flush all pending updates immediately
   * Called when user clicks Complete button or manually syncs
   */
  async forceBatchUpdateFlush(): Promise<void> {
    console.log('[Batch Update] Force flush requested');
    await this.flushBatchUpdate();
  }

  /**
   * Admin override to reset scan status
   */
  async adminOverrideScan(): Promise<void> {
    if (!this.duplicateScanInfo || !this.isAdminUser) {
      return;
    }

    const cartonNumber = this.duplicateScanInfo.cartonNumber;

    try {
      const response: any = await this.http.post<any>(
        `${environment.apiUrl}/api/mobile-scanning/override-scan/${cartonNumber}`,
        { reason: 'Manual override from mobile scanner' }
      ).toPromise();

      this.showAlertMessage('Scan status reset successfully', 'success');
      this.closeDuplicateWarning();

      // Rescan automatically after override
      setTimeout(() => {
        this.manualCartonNo = cartonNumber;
        this.addManualEntry();
      }, 500);

    } catch (error) {
      console.error('Failed to override scan:', error);
      this.showAlertMessage('Failed to reset scan status', 'error');
    }
  }

  // ============================================================================
  // === NEW: QUAGGA2 BARCODE SCANNER METHODS ===
  // ============================================================================

  /**
   * Initialize Quagga2 barcode scanner for improved 1D barcode scanning
   */
  initializeQuagga2Scanner(): void {
    if (this.quaggaInitialized || !this.quaggaScannerElement) {
      return;
    }

    console.log('[Quagga2] Initializing scanner...');
    console.log('[Quagga2] Selected device:', this.selectedDevice?.label || this.selectedDevice?.deviceId || 'Not set');

    // Get platform-specific constraints (Android 15 fix)
    const cameraConstraints = this.getCameraConstraints();

    Quagga.init({
      inputStream: {
        name: 'Live',
        type: 'LiveStream',
        target: this.quaggaScannerElement.nativeElement,
        constraints: {
          ...cameraConstraints,
          aspectRatio: { ideal: 16/9 }
        },
        area: {
          top: '0%',
          right: '0%',
          left: '0%',
          bottom: '0%'
        }
      },
      decoder: {
        readers: [
          'code_128_reader',  // Most common for cartons - prioritize this
          'code_39_reader'    // Alternative carton format
        ],
        debug: {
          drawBoundingBox: true,
          showFrequency: false,
          drawScanline: true,
          showPattern: false
        },
        multiple: false  // Only decode one barcode at a time for accuracy
      },
      locate: true, // Enable barcode locator for better angle tolerance
      locator: {
        patchSize: 'medium',  // Balance between speed and accuracy
        halfSample: true      // Improves performance with minimal accuracy loss
      },
      numOfWorkers: 2,  // Use 2 workers for better performance
      frequency: 5  // Reduced from 10 - fewer scans but higher quality
    }, async (err) => {
      if (err) {
        console.error('[Quagga2] Initialization failed:', err);
        console.error('[Quagga2] Error details:', {
          name: err.name,
          message: err.message,
          deviceId: this.selectedDevice?.deviceId,
          label: this.selectedDevice?.label
        });

        // Android 15 fix: Try alternative camera before giving up
        if (this.availableDevices.length > 1) {
          console.log('[Quagga2] Attempting retry with alternative camera...');
          await this.retryQuaggaInitialization();
        } else {
          // No alternative cameras available
          console.log('[Quagga2] No alternative cameras, falling back to manual entry');
          this.scannerMode = { mode: 'manual', reason: 'Camera initialization failed - no alternative cameras' };
          this.usingQuagga2 = false;
          this.manualEntry = true;
          this.showAlertMessage('Camera scanner failed. Please use manual entry.', 'warning');
        }
        return;
      }

      console.log('[Quagga2] Initialization successful');
      this.quaggaInitialized = true;
      this.usingQuagga2 = true;
      this.scannerMode = { mode: 'quagga' };

      // Start scanning
      Quagga.start();
      console.log('[Quagga2] Scanner started - waiting for barcode detections...');

      // Add frame processing monitor to verify Quagga2 is analyzing frames
      let frameCount = 0;
      let lastLogTime = Date.now();
      let scanStartTime = Date.now();
      let barcodeDetected = false;
      let ocrTriggered = false;

      Quagga.onProcessed((result: any) => {
        frameCount++;
        const now = Date.now();

        // Check if 5 seconds have passed without barcode detection (check on every frame)
        if (!barcodeDetected && !ocrTriggered && (now - scanStartTime) >= 5000) {
          console.log('[Quagga2] ⏱️ 5 seconds elapsed without barcode detection - triggering OCR fallback');
          ocrTriggered = true; // Prevent multiple OCR triggers
          this.triggerOcrFallback();

          // Reset for next OCR attempt (every 5 seconds)
          setTimeout(() => {
            ocrTriggered = false;
            scanStartTime = Date.now();
          }, 5000);
        }

        // Log processing status every 5 seconds
        if (now - lastLogTime > 5000) {
          console.log(`[Quagga2] Processing frames: ${frameCount} frames analyzed in last 5 seconds`);

          if (result) {
            console.log('[Quagga2] Frame analysis active:', {
              boxes: result.boxes?.length || 0,
              box: result.box ? 'Found' : 'Not found',
              codeResult: result.codeResult ? 'Barcode detected!' : 'No barcode in frame'
            });
          } else {
            console.warn('[Quagga2] ⚠️ Frame processing result is null - camera may not be working');
          }

          frameCount = 0;
          lastLogTime = now;
        }
      });

      // Register barcode detection callback with quality-based priority
      let lastDetectedCode = '';
      let lastDetectionTime = 0;
      const DEBOUNCE_TIME = 1000; // 1 second between same code detections
      const HOLD_TIME = 2000; // Hold medium-quality barcodes for 2s to see if OCR finds better result

      Quagga.onDetected((result: any) => {
        if (result && result.codeResult && result.codeResult.code) {
          const code = result.codeResult.code;
          const now = Date.now();

          // Prevent duplicate rapid scans of the same code
          if (code === lastDetectedCode && (now - lastDetectionTime) < DEBOUNCE_TIME) {
            console.log('[Quagga2] Duplicate scan ignored (debounced)');
            return;
          }

          // Get quality metrics from the result
          const format = result.codeResult.format || 'unknown';
          const startInfo = result.codeResult.startInfo || {};
          const endInfo = result.codeResult.endInfo || {};

          console.log('[Quagga2] Barcode detected:', {
            code: code,
            format: format,
            length: code.length,
            rawCode: JSON.stringify(code),
            startInfo: startInfo,
            endInfo: endInfo
          });

          // Strict validation for carton numbers
          const trimmedCode = code.trim();
          const isValidLength = trimmedCode.length >= 8 && trimmedCode.length <= 50;
          const isNotEmpty = trimmedCode.length > 0;
          const isAlphanumeric = /^[A-Z0-9-]+$/i.test(trimmedCode);
          const hasCartonPattern = /^[A-Z]{2,}[A-Z0-9-]{6,}$/i.test(trimmedCode);

          console.log('[Quagga2] Validation check:', {
            code: trimmedCode,
            isValidLength: isValidLength,
            isNotEmpty: isNotEmpty,
            isAlphanumeric: isAlphanumeric,
            hasCartonPattern: hasCartonPattern,
            willProcess: isValidLength && isNotEmpty && isAlphanumeric && hasCartonPattern
          });

          // MUST pass ALL validations to avoid garbage like "&/"
          if (isValidLength && isNotEmpty && isAlphanumeric && hasCartonPattern) {
            // Calculate quality score to determine processing strategy
            const quality = this.calculateBarcodeQuality(result);

            if (quality >= 0.8) {
              // High quality barcode - process immediately
              barcodeDetected = true;
              lastDetectedCode = trimmedCode;
              lastDetectionTime = now;
              console.log('[Quagga2] ✅ High quality barcode - processing immediately (quality:', quality.toFixed(2), ')');
              this.processCartonScan(trimmedCode);

              // Cancel any pending low-quality barcode
              if (this.pendingBarcodeResult) {
                clearTimeout(this.pendingBarcodeResult.timeout);
                this.pendingBarcodeResult = null;
                console.log('[Quagga2] Cancelled pending low-quality barcode');
              }
            } else if (quality >= 0.5) {
              // Medium quality - hold for OCR comparison
              console.log('[Quagga2] ⏸️ Medium quality barcode - holding for', HOLD_TIME, 'ms (quality:', quality.toFixed(2), ')');

              // Cancel previous pending barcode if any
              if (this.pendingBarcodeResult) {
                clearTimeout(this.pendingBarcodeResult.timeout);
              }

              // Set new pending barcode with timeout
              const timeout = setTimeout(() => {
                if (this.pendingBarcodeResult && !barcodeDetected) {
                  console.log('[Quagga2] ✅ Processing held barcode (no better OCR found):', this.pendingBarcodeResult.code);
                  this.processCartonScan(this.pendingBarcodeResult.code);
                  barcodeDetected = true;
                  lastDetectedCode = this.pendingBarcodeResult.code;
                  lastDetectionTime = Date.now();
                  this.pendingBarcodeResult = null;
                }
              }, HOLD_TIME);

              this.pendingBarcodeResult = { code: trimmedCode, quality: quality, timeout: timeout };
            } else {
              // Low quality - reject
              console.warn('[Quagga2] ❌ Low quality barcode rejected (quality:', quality.toFixed(2), '):', trimmedCode);
            }
          } else {
            console.warn('[Quagga2] ❌ Barcode validation failed:', {
              code: trimmedCode,
              reason: !isValidLength ? 'Invalid length (need 8-50 chars)'
                    : !isAlphanumeric ? 'Contains invalid characters (need alphanumeric only)'
                    : !hasCartonPattern ? 'Does not match carton pattern (2+ letters + 6+ alphanumeric)'
                    : 'Empty or whitespace only',
              length: trimmedCode.length,
              isAlphanumeric: isAlphanumeric,
              hasCartonPattern: hasCartonPattern
            });
          }
        }
      });

      // Force CSS overrides after Quagga2 injects DOM elements
      // Small delay ensures DOM is fully updated
      setTimeout(() => {
        this.forceQuaggaStyleOverrides();
      }, 100);
    });
  }

  /**
   * Forces CSS overrides on Quagga2-injected video and canvas elements.
   * This method must be called AFTER Quagga.init() completes and injects
   * the video/canvas elements into the DOM. It uses style.setProperty()
   * with 'important' flag to override Quagga2's inline styles.
   */
  private forceQuaggaStyleOverrides(): void {
    const scanner = document.getElementById('quagga-scanner');
    if (!scanner) {
      console.warn('⚠️ Quagga scanner container not found');
      return;
    }

    // Force video element styles
    const video = scanner.querySelector('video');
    if (video) {
      // Remove conflicting HTML attributes that Quagga2 might have set
      video.removeAttribute('width');
      video.removeAttribute('height');

      // Force CSS properties with !important flag
      video.style.setProperty('width', '100%', 'important');
      video.style.setProperty('height', '100%', 'important');
      video.style.setProperty('object-fit', 'cover', 'important');
      video.style.setProperty('position', 'absolute', 'important');
      video.style.setProperty('top', '0', 'important');
      video.style.setProperty('left', '0', 'important');
      video.style.setProperty('display', 'block', 'important');

      // Log verification data
      const computedStyles = window.getComputedStyle(video);
      console.log('✅ Video styles overridden successfully:', {
        computedWidth: computedStyles.width,
        computedHeight: computedStyles.height,
        computedObjectFit: computedStyles.objectFit,
        videoStreamWidth: (video as HTMLVideoElement).videoWidth,
        videoStreamHeight: (video as HTMLVideoElement).videoHeight,
        intrinsicAspectRatio: ((video as HTMLVideoElement).videoWidth / (video as HTMLVideoElement).videoHeight).toFixed(3)
      });
    } else {
      console.warn('⚠️ Video element not found in Quagga scanner');
    }

    // Force canvas element styles
    const canvases = scanner.querySelectorAll('canvas');
    if (canvases.length > 0) {
      canvases.forEach((canvas: HTMLCanvasElement, index: number) => {
        // Remove conflicting HTML attributes
        canvas.removeAttribute('width');
        canvas.removeAttribute('height');

        // Force CSS properties with !important flag
        canvas.style.setProperty('width', '100%', 'important');
        canvas.style.setProperty('height', '100%', 'important');
        canvas.style.setProperty('object-fit', 'cover', 'important');
        canvas.style.setProperty('position', 'absolute', 'important');
        canvas.style.setProperty('top', '0', 'important');
        canvas.style.setProperty('left', '0', 'important');

        console.log(`✅ Canvas ${index} styles overridden successfully`);
      });
    } else {
      console.log('ℹ️ No canvas elements found (may be created later)');
    }

    console.log('✅ Quagga2 element style overrides completed');
  }

  /**
   * Cleanup Quagga2 scanner
   */
  async cleanupQuagga2(): Promise<void> {
    if (this.quaggaInitialized) {
      console.log('[Quagga2] Stopping scanner...');
      Quagga.stop();

      // Android 15: Wait for camera stream to be fully released
      // This prevents "black screen" when switching cameras
      await new Promise(resolve => setTimeout(resolve, 300));

      this.quaggaInitialized = false;
      this.usingQuagga2 = false;
      console.log('[Quagga2] ✅ Camera fully released');
    }
  }

  /**
   * Retry camera initialization with fallback constraints
   * Android 15 specific: Try different camera if primary fails
   * This fixes "Camera scanner failed to initialize" error
   */
  private async retryQuaggaInitialization(): Promise<void> {
    console.log('[Quagga2] 🔄 Retrying with alternative camera...');

    // If back camera failed, try front camera (or vice versa)
    if (this.selectedDevice && this.availableDevices.length > 1) {
      const currentIndex = this.availableDevices.findIndex(
        d => d.deviceId === this.selectedDevice?.deviceId
      );
      const nextIndex = (currentIndex + 1) % this.availableDevices.length;
      this.selectedDevice = this.availableDevices[nextIndex];

      console.log(`[Quagga2] Switching to camera ${nextIndex}: ${this.selectedDevice.label || this.selectedDevice.deviceId}`);

      // Wait a moment for previous camera to fully release
      await new Promise(resolve => setTimeout(resolve, 500));

      // Try initializing with new camera
      this.initializeQuagga2Scanner();
    } else {
      // No alternative camera, fallback to manual entry
      console.log('[Quagga2] ❌ No alternative cameras available, switching to manual entry');
      this.scannerMode = { mode: 'manual', reason: 'All cameras failed - manual entry required' };
      this.manualEntry = true;
      this.showAlertMessage('Camera initialization failed. Please use manual entry.', 'warning');
    }
  }

  /**
   * Learn carton prefix patterns from successfully scanned cartons
   * Used for smart OCR auto-completion
   */
  private learnCartonPrefix(cartonNo: string): void {
    // Extract prefix: letters before digits
    // Examples: "TWSPKM25100406517" → "TWSPKM", "ABC123XYZ456" → "ABC"
    const prefixMatch = cartonNo.match(/^([A-Z]+)/i);

    if (prefixMatch && prefixMatch[1].length >= 2) {
      const prefix = prefixMatch[1].toUpperCase();
      this.detectedCartonPrefixes.add(prefix);

      // Update common prefix to most recently learned (prioritize latest pattern)
      this.commonCartonPrefix = prefix;

      console.log('[Prefix Learning] Detected prefix:', prefix, '| Known prefixes:', Array.from(this.detectedCartonPrefixes));
    }
  }

  /**
   * Get the most appropriate prefix for OCR auto-completion
   * Returns the most recently learned prefix, or tries to match partial prefixes
   */
  private getCartonPrefixForCompletion(partialPrefix?: string): string {
    // If we have a partial prefix, try to find a matching learned prefix
    if (partialPrefix) {
      for (const knownPrefix of this.detectedCartonPrefixes) {
        if (knownPrefix.startsWith(partialPrefix)) {
          console.log('[Prefix Match] Partial prefix', partialPrefix, 'matched with learned prefix:', knownPrefix);
          return knownPrefix;
        }
      }
    }

    // Return most recently learned prefix, or default
    const prefix = this.commonCartonPrefix;
    console.log('[Prefix Selection] Using prefix:', prefix, '(from', this.detectedCartonPrefixes.size, 'learned patterns)');
    return prefix;
  }

  /**
   * Calculate barcode quality score (0.0 to 1.0)
   * Used to prioritize high-quality barcodes over OCR fallback
   */
  private calculateBarcodeQuality(result: any): number {
    const code = result.codeResult.code.trim();
    let score = 0;

    // Factor 1: Length score (optimal 10-20 chars for carton numbers)
    const length = code.length;
    if (length >= 10 && length <= 20) {
      score += 0.3; // Perfect length
    } else if (length >= 8 && length <= 25) {
      score += 0.15; // Acceptable length
    } else {
      score += 0; // Too short or too long
    }

    // Factor 2: Character composition (alphanumeric only is best)
    const isAlphanumeric = /^[A-Z0-9-]+$/i.test(code);
    if (isAlphanumeric) {
      score += 0.4; // Clean alphanumeric data
    } else if (/^[A-Z0-9\s-]+$/i.test(code)) {
      score += 0.2; // Has spaces but otherwise clean
    } else {
      score += 0; // Contains special characters - likely garbage
    }

    // Factor 3: Pattern match (carton number pattern)
    const hasCartonPattern = /^[A-Z]{2,}[A-Z0-9-]{6,}$/i.test(code);
    if (hasCartonPattern) {
      score += 0.3; // Matches expected carton format
    } else {
      score += 0; // Doesn't match pattern
    }

    console.log('[Barcode Quality]', {
      code: code,
      length: length,
      isAlphanumeric: isAlphanumeric,
      hasCartonPattern: hasCartonPattern,
      qualityScore: score.toFixed(2)
    });

    return score;
  }

  /**
   * Trigger OCR fallback when barcode detection fails after 5 seconds
   */
  async triggerOcrFallback(): Promise<void> {
    console.log('[OCR Fallback] Triggering OCR to capture text below barcode...');

    // Get video element from Quagga scanner
    const scanner = document.getElementById('quagga-scanner');
    if (!scanner) {
      console.warn('[OCR Fallback] Scanner container not found');
      return;
    }

    const videoElement = scanner.querySelector('video') as HTMLVideoElement;
    if (!videoElement) {
      console.warn('[OCR Fallback] Video element not found');
      return;
    }

    // Initialize Tesseract worker if not already done
    if (!this.tesseractWorker) {
      await this.initializeTesseractWorker();
    }

    // Double-check worker is initialized
    if (!this.tesseractWorker) {
      console.error('[OCR Fallback] Worker initialization failed');
      return;
    }

    try {
      this.ocrProcessing = true;
      this.scanningStatus = 'Barcode not detected. Scanning text below barcode...';
      this.ocrProgress = 0;

      // Capture current video frame
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(videoElement, 0, 0);

      // Crop to bottom third (where text typically is)
      const croppedCanvas = this.cropToTextRegion(canvas);

      // Enhance image for better OCR
      const enhancedCanvas = this.enhanceImageForOCR(croppedCanvas);

      // Run Tesseract OCR
      const { data: { text, confidence } } = await this.tesseractWorker.recognize(
        enhancedCanvas
      );

      console.log('[OCR Fallback] Raw result:', text, 'Confidence:', confidence);

      // First, try to extract/auto-complete carton number
      const cleanText = this.cleanupOCRText(text);

      // Check if we got a valid carton number (even with auto-completion)
      if (cleanText.length >= 10) {  // Minimum valid carton number length
        // Check if this was auto-completed from partial match (numbers only in raw OCR text)
        const rawNumbers = text.replace(/[^0-9]/g, '');
        const rawLetters = text.replace(/[^A-Z]/gi, '');

        // Auto-completed if: mostly/only numbers in raw text, but result has prefix
        const wasAutoCompleted = rawNumbers.length >= 8 && rawLetters.length < 3;

        if (wasAutoCompleted) {
          // Auto-completed from numeric pattern - prioritize even with low confidence
          console.log('[OCR Fallback] 🎯 Auto-completed carton number detected - showing for confirmation:', cleanText);

          // Cancel any pending barcode
          if (this.pendingBarcodeResult) {
            clearTimeout(this.pendingBarcodeResult.timeout);
            console.log('[OCR vs Barcode] ✅ OCR auto-completion wins! Cancelling pending barcode');
            this.pendingBarcodeResult = null;
          }

          // Always show confirmation for auto-completed (even low confidence OCR)
          this.currentOcrResult = {
            text: cleanText,
            confidence: confidence,
            timestamp: new Date()
          };

          const confirmed = await this.confirmOcrResult(cleanText, confidence);
          if (confirmed) {
            console.log('[OCR Fallback] ✅ User confirmed auto-completed text:', cleanText);
            this.processCartonScan(cleanText);
          }
        } else if (confidence >= 80) {
          // High confidence with full match - auto-process
          if (this.pendingBarcodeResult) {
            clearTimeout(this.pendingBarcodeResult.timeout);
            console.log('[OCR vs Barcode] ✅ OCR wins! Cancelling pending barcode:', {
              pendingBarcode: this.pendingBarcodeResult.code,
              pendingQuality: this.pendingBarcodeResult.quality.toFixed(2),
              ocrText: cleanText,
              ocrConfidence: confidence
            });
            this.pendingBarcodeResult = null;
          }

          console.log('[OCR Fallback] ✅ High confidence text detected (auto-processing):', cleanText);
          this.processCartonScan(cleanText);
        } else if (confidence >= 35) {
          // Medium/Low confidence - show confirmation (lowered threshold with better validation)
          this.currentOcrResult = {
            text: cleanText,
            confidence: confidence,
            timestamp: new Date()
          };

          const confirmed = await this.confirmOcrResult(cleanText, confidence);
          if (confirmed) {
            console.log('[OCR Fallback] ✅ User confirmed text:', cleanText);
            this.processCartonScan(cleanText);
          }
        } else {
          console.log('[OCR Fallback] ❌ Very low confidence, skipping:', confidence);
        }
      } else {
        console.log('[OCR Fallback] ❌ Text too short for carton number:', cleanText, `(${cleanText.length} chars)`);
      }
    } catch (error) {
      console.error('[OCR Fallback] Detection failed:', error);
    } finally {
      this.ocrProcessing = false;
      this.ocrProgress = 0;
      this.scanningStatus = '';
    }
  }

  // ============================================================================
  // === NEW: TESSERACT.JS OCR METHODS ===
  // ============================================================================

  /**
   * Initialize Tesseract OCR worker (reusable for performance)
   */
  async initializeTesseractWorker(): Promise<void> {
    try {
      console.log('[Tesseract] Initializing OCR worker...');

      this.tesseractWorker = await createWorker('eng', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            this.ocrProgress = Math.round(m.progress * 100);
          }
        }
      });

      // Configure for alphanumeric carton labels (patterns like TWSPKM25092503210)
      await this.tesseractWorker.setParameters({
        tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-',
        tessedit_pageseg_mode: Tesseract.PSM.AUTO // Auto-detect text layout (not limited to single line)
      });

      console.log('[Tesseract] OCR worker ready');
    } catch (error) {
      console.error('[Tesseract] Failed to initialize worker:', error);
    }
  }

  /**
   * Cleanup Tesseract worker
   */
  async cleanupTesseractWorker(): Promise<void> {
    if (this.tesseractWorker) {
      console.log('[Tesseract] Terminating worker...');
      await this.tesseractWorker.terminate();
      this.tesseractWorker = undefined;
    }
  }

  /**
   * Attempt OCR text detection as fallback when barcode scanning fails
   */
  async attemptOcrFallback(videoElement?: HTMLVideoElement): Promise<string | null> {
    if (!this.tesseractWorker) {
      console.warn('[OCR] Worker not initialized');
      return null;
    }

    try {
      this.ocrProcessing = true;
      this.scanningStatus = 'Barcode not detected. Scanning text below barcode...';
      this.ocrProgress = 0;

      // Capture current video frame
      const canvas = document.createElement('canvas');
      const video = videoElement || document.querySelector('video');

      if (!video) {
        console.warn('[OCR] No video element found');
        return null;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(video, 0, 0);

      // Step 1: Crop to bottom third (where text typically is)
      const croppedCanvas = this.cropToTextRegion(canvas);

      // Step 2: Enhance image for better OCR
      const enhancedCanvas = this.enhanceImageForOCR(croppedCanvas);

      // Step 3: Run Tesseract OCR
      const { data: { text, confidence } } = await this.tesseractWorker.recognize(
        enhancedCanvas
      );

      console.log('[OCR] Result:', text, 'Confidence:', confidence);

      if (confidence > 70) { // Minimum 70% confidence
        const cleanText = this.cleanupOCRText(text);

        if (cleanText.length >= 5) { // Minimum 5 characters
          this.currentOcrResult = {
            text: cleanText,
            confidence: confidence,
            timestamp: new Date()
          };

          // Show confirmation dialog
          const confirmed = await this.confirmOcrResult(cleanText, confidence);

          if (confirmed) {
            return cleanText;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('[OCR] Detection failed:', error);
      return null;
    } finally {
      this.ocrProcessing = false;
      this.ocrProgress = 0;
    }
  }

  /**
   * Crop canvas to bottom half where text is typically located
   * (Independent of barcode - scans entire lower region for text patterns)
   */
  cropToTextRegion(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext('2d')!;
    const height = canvas.height;
    const textRegionHeight = height / 2; // Bottom half for better text coverage

    const cropped = document.createElement('canvas');
    cropped.width = canvas.width;
    cropped.height = textRegionHeight;

    const croppedCtx = cropped.getContext('2d')!;
    croppedCtx.drawImage(
      canvas,
      0, height - textRegionHeight, // Source position
      canvas.width, textRegionHeight, // Source dimensions
      0, 0, // Destination position
      canvas.width, textRegionHeight // Destination dimensions
    );

    return cropped;
  }

  /**
   * Enhance image for better OCR accuracy
   */
  enhanceImageForOCR(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Convert to grayscale and increase contrast
    for (let i = 0; i < data.length; i += 4) {
      // Calculate grayscale value
      const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;

      // Apply threshold for high contrast
      const threshold = 128;
      const value = avg < threshold ? 0 : 255;

      data[i] = value;     // R
      data[i + 1] = value; // G
      data[i + 2] = value; // B
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  /**
   * Clean up OCR text result and extract carton number patterns
   * Handles multi-line text like: "ILEUS\n\nTWSPKM25100406517\n\nPH P3"
   * Also handles partial matches like ".23090206200" → "TWSPKM23090206200"
   */
  cleanupOCRText(text: string): string {
    console.log('[OCR] Raw text input:', JSON.stringify(text));

    // Split by newlines, spaces, and other whitespace to get individual tokens
    const tokens = text.split(/[\n\r\s]+/).filter(t => t.length > 0);
    console.log('[OCR] Split tokens:', tokens);

    const candidates: string[] = [];

    // Check each token for carton number pattern
    for (const token of tokens) {
      const cleaned = token
        .replace(/[^A-Z0-9-]/gi, '') // Keep only alphanumeric and hyphen
        .toUpperCase()
        .trim();

      if (cleaned.length === 0) continue;

      // Full carton pattern: 2+ letters followed by 6+ alphanumeric chars (total 8+ chars)
      // Must contain at least 3 digits to avoid garbage text like "BNRRLUENIRLIRLERLLLELLLLIL"
      // Examples: TWSPKM25100406517 ✅, ABC123XYZ ✅, BNRRLUENIRLIRLERLLLELLLLIL ❌ (no digits)
      const fullCartonPattern = /^[A-Z]{2,}[A-Z0-9-]{6,}$/;
      const hasMinimumDigits = (cleaned.match(/\d/g) || []).length >= 3;

      if (fullCartonPattern.test(cleaned) && hasMinimumDigits) {
        candidates.push(cleaned);
        console.log('[OCR] Found full carton candidate:', cleaned);
        continue;
      } else if (fullCartonPattern.test(cleaned) && !hasMinimumDigits) {
        console.log('[OCR] Rejected candidate (no digits, likely garbage):', cleaned);
        continue;
      }

      // Partial pattern: Detect numbers-only sequences that match carton suffix
      // Pattern: 8+ digits (likely missing letter prefix)
      // Example: "23090206200" → "TWSPKM23090206200" (using learned prefix)
      const partialNumericPattern = /^(\d{8,})$/;
      const partialMatch = cleaned.match(partialNumericPattern);

      if (partialMatch) {
        // Auto-complete with learned prefix (dynamic, not hardcoded!)
        const learnedPrefix = this.getCartonPrefixForCompletion();
        const completed = learnedPrefix + partialMatch[1];
        candidates.push(completed);
        console.log('[OCR] ⚡ Found partial match (numbers only), auto-completed with learned prefix:', partialMatch[1], '→', completed);
        continue;
      }

      // Another partial pattern: Starts with 1-5 letters + 8+ digits
      // Example: "M23090206200" → "TWSPKM23090206200" (match learned prefix)
      const partialPrefixPattern = /^([A-Z]{1,5})(\d{8,})$/;
      const prefixMatch = cleaned.match(partialPrefixPattern);

      if (prefixMatch) {
        const detectedLetters = prefixMatch[1];
        const numbers = prefixMatch[2];

        // Try to match with learned prefixes
        const matchedPrefix = this.getCartonPrefixForCompletion(detectedLetters);

        if (matchedPrefix.startsWith(detectedLetters)) {
          const completed = matchedPrefix + numbers;
          candidates.push(completed);
          console.log('[OCR] ⚡ Found partial prefix match, auto-completed with learned prefix:', cleaned, '→', completed);
          continue;
        }

        // Otherwise keep as-is if it's valid length
        if (cleaned.length >= 8) {
          candidates.push(cleaned);
          console.log('[OCR] Found partial carton candidate:', cleaned);
        }
      }
    }

    // Smart selection: Prioritize learned prefixes over just length
    if (candidates.length > 0) {
      // Strategy 1: Check if any candidate matches a learned prefix
      for (const candidate of candidates) {
        for (const learnedPrefix of this.detectedCartonPrefixes) {
          if (candidate.startsWith(learnedPrefix)) {
            console.log('[OCR] ✅ Extracted carton number (matched learned prefix):', candidate, `(${candidate.length} chars, prefix: ${learnedPrefix})`);
            return candidate;
          }
        }
      }

      // Strategy 2: Check if any candidate matches the common prefix pattern
      const prefixPattern = new RegExp(`^${this.commonCartonPrefix}`);
      for (const candidate of candidates) {
        if (prefixPattern.test(candidate)) {
          console.log('[OCR] ✅ Extracted carton number (matched common prefix):', candidate, `(${candidate.length} chars)`);
          return candidate;
        }
      }

      // Strategy 3: Prioritize candidates with numbers (more likely carton numbers)
      const candidatesWithNumbers = candidates.filter(c => /\d{8,}/.test(c));
      if (candidatesWithNumbers.length > 0) {
        const longest = candidatesWithNumbers.sort((a, b) => b.length - a.length)[0];
        console.log('[OCR] ✅ Extracted carton number (has 8+ digits):', longest, `(${longest.length} chars)`);
        return longest;
      }

      // Strategy 4: Fallback to longest match (least reliable)
      const longest = candidates.sort((a, b) => b.length - a.length)[0];
      console.log('[OCR] ⚠️ Extracted carton number (longest, no better match):', longest, `(${longest.length} chars)`);
      return longest;
    }

    console.log('[OCR] ❌ No valid carton pattern found in text');
    return '';
  }

  /**
   * Show confirmation dialog for OCR result
   */
  async confirmOcrResult(text: string, confidence: number): Promise<boolean> {
    return new Promise((resolve) => {
      this.currentOcrResult = {
        text: text,
        confidence: confidence,
        timestamp: new Date()
      };

      this.showOcrConfirmDialog = true;
      this.ocrConfirmCallback = resolve;
    });
  }

  /**
   * User confirmed OCR result - use it
   */
  confirmOcrUse(): void {
    this.showOcrConfirmDialog = false;
    if (this.ocrConfirmCallback) {
      this.ocrConfirmCallback(true);
      this.ocrConfirmCallback = undefined;
    }
  }

  /**
   * User rejected OCR result - retry
   */
  retryOcr(): void {
    this.showOcrConfirmDialog = false;
    if (this.ocrConfirmCallback) {
      this.ocrConfirmCallback(false);
      this.ocrConfirmCallback = undefined;
    }
  }

  /**
   * Switch to manual entry mode and copy OCR text for editing
   */
  async switchToManualEntry(): Promise<void> {
    this.showOcrConfirmDialog = false;

    // Copy OCR-detected text to manual input for editing
    if (this.currentOcrResult?.text) {
      this.manualCartonNo = this.currentOcrResult.text;
      console.log('[Manual Entry] Copied OCR text to manual input:', this.currentOcrResult.text);
    }

    this.manualEntry = true;

    // Stop camera scanner to allow manual entry (Android 15: wait for cleanup)
    if (this.cameraScannerActive) {
      await this.stopCameraScanner();
    }

    // Reject the OCR callback
    if (this.ocrConfirmCallback) {
      this.ocrConfirmCallback(false);
      this.ocrConfirmCallback = undefined;
    }
  }
}
